import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useGeneralSettings } from '../../hooks/useGeneralSettings';
import { DateRangePresetFilter } from '../shared/DateRangePresetFilter';
import { deviceDisplayName } from '../../utils/deviceName';
import { formatDT, formatDateOnly, resolveTimezone, getFileTimestamp, zonedDateTimeToUtc, getZonedParts } from '../../utils/timezone';
import { ConfirmActionDialog } from '../shared/ConfirmActionDialog';
import { DuplicateScheduleDialog } from './DuplicateScheduleDialog';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { PaginationFooter } from '../shared/PaginationFooter';
import { useUrlPage, useUrlPageSize, useSetUrlParams } from '../shared/useUrlPagination';
import { DevicePicker } from '../shared/DevicePicker';
import { NotificationsConfig } from '../events/NotificationsConfig';
import { WebhookActionConfig } from '../events/WebhookActionConfig';
import ScheduleImportWizard, { downloadScheduleTemplateXlsx, downloadScheduleTemplateCsv, exportSchedules } from '../wizards/ScheduleImportWizard';
import { ScheduleHelpPanel, LogServiceHelpPanel, EditServiceLogHelpPanel, MaintHelpTip } from './MaintenanceHelp';
import { CostTrends } from './CostTrends';
import { CommandActionConfig } from '../events/CommandActionConfig';
import { NotificationTemplateModal } from '../events/NotificationTemplateModal';
import { rowsToCsv, downloadBlob, exportTableHtml, newLandscapeDoc, addBrandHeader, addFooters, loadLogoPng, drawPdfTable } from '../../utils/tripExport';
import {
  Wrench, Plus, Pencil, Trash2, CheckCircle, Gauge, Clock, Calendar as CalIcon,
  AlertTriangle, Loader2, ChevronDown, ChevronRight, Upload, Paperclip, X, Search,
  RefreshCw, History, Bell, Download, Truck, Copy, DollarSign,
  FileSpreadsheet, Sparkles, Undo2,
} from 'lucide-react';
import { toast } from 'sonner';

const BASIS_META = {
  odometer: { label: 'Odometer', unit: 'km', icon: Gauge },
  engine_hours: { label: 'Engine Hours', unit: 'hrs', icon: Clock },
  calendar: { label: 'Calendar', unit: 'days', icon: CalIcon },
  odometer_calendar: { label: 'Odometer + Calendar', unit: 'km', icon: Gauge },
  engine_hours_calendar: { label: 'Engine Hours + Calendar', unit: 'hrs', icon: Clock },
};

const isDualBasis = (b) => b === 'odometer_calendar' || b === 'engine_hours_calendar';
const usesOdometer = (b) => b === 'odometer' || b === 'odometer_calendar';
const usesEngineHours = (b) => b === 'engine_hours' || b === 'engine_hours_calendar';

const STATUS_STYLES = {
  ok: 'bg-green-50 text-green-700 border-green-200',
  due_soon: 'bg-amber-50 text-amber-700 border-amber-200',
  overdue: 'bg-red-50 text-red-700 border-red-200',
  completed: 'bg-slate-100 text-slate-600 border-slate-200',
};

const SEVERITY_STYLES = {
  low: 'bg-slate-100 text-slate-700 border-slate-200',
  medium: 'bg-blue-50 text-blue-700 border-blue-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200',
  critical: 'bg-red-50 text-red-700 border-red-200',
};

// Rotating tints so stacked stage cards are easy to tell apart in the editor
const STAGE_CARD_STYLES = [
  'bg-rose-50/70 border-rose-200 dark:bg-rose-950/25 dark:border-rose-900',
  'bg-emerald-50/70 border-emerald-200 dark:bg-emerald-950/25 dark:border-emerald-900',
  'bg-amber-50/70 border-amber-200 dark:bg-amber-950/25 dark:border-amber-900',
  'bg-violet-50/70 border-violet-200 dark:bg-violet-950/25 dark:border-violet-900',
  'bg-sky-50/70 border-sky-200 dark:bg-sky-950/25 dark:border-sky-900',
];

const DEFAULT_STAGE_NOTIF = { system_message: true, auto_hide: false, sound: false, notify_bell: true };
const DEFAULT_STAGE_ACTIONS = {
  webhook_enabled: false, webhook_method: 'POST', webhook_url: '', webhook_auth_type: 'none',
  webhook_auth_token: '', webhook_headers: [], webhook_payload_mode: 'auto', webhook_custom_payload: '',
  webhook_retry_count: 0, webhook_retry_delay_secs: 5,
  command_on_trigger_enabled: false,
  command_on_trigger: { source: 'custom', command_text: '', command_mode: 'single', command_sequence: [], delivery_method: 'gprs', device_command_id: '', template_id: '', send_to: 'this_device', target_device_ids: [] },
};

const makeStage = () => ({
  id: (crypto.randomUUID ? crypto.randomUUID() : `stage_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`),
  label: '', trigger_point: 'before', trigger_value: 0, trigger_days: 0, severity: 'medium', raise_alert: true,
  notifications: { ...DEFAULT_STAGE_NOTIF }, actions: JSON.parse(JSON.stringify(DEFAULT_STAGE_ACTIONS)),
});

const emptyForm = () => ({
  device_id: '', task_name: '', task_preset: 'service', basis: 'odometer',
  interval_value: '', interval_days: '', recurring: true, is_active: true, fixed_schedule: true,
  baseline_value: '', baseline_date: '', notes: '', reminder_stages: [makeStage()], attachments: [],
});

export const MaintenanceTab = () => {
  const { api } = useAuth();
  const { dateFormat: userDateFormat } = useGeneralSettings();
  const { isNotificationChannelActive, getNotificationDisabledReason } = useFeatureFlags();
  const hasPushNotif = isNotificationChannelActive('notification_push');
  const hasEmailNotif = isNotificationChannelActive('notification_email');
  const hasSmsNotif = isNotificationChannelActive('notification_sms');
  const pushDisabledReason = getNotificationDisabledReason('notification_push');
  const emailDisabledReason = getNotificationDisabledReason('notification_email');
  const smsDisabledReason = getNotificationDisabledReason('notification_sms');

  // Session memory — remember the Maintenance sub-view, searches, status filter and
  // sort across navigation & F5 (same sessionStorage approach as Fleet/People/Analytics).
  const _mtnMem = (() => {
    try { return JSON.parse(sessionStorage.getItem('maintenance_filters') || '{}'); } catch { return {}; }
  })();
  const [view, setView] = useState(['schedules', 'history', 'cost'].includes(_mtnMem.view) ? _mtnMem.view : 'schedules');
  const [devices, setDevices] = useState([]);
  const [presets, setPresets] = useState([]);
  const [notifTemplates, setNotifTemplates] = useState([]);
  const [summary, setSummary] = useState(null);

  const [schedules, setSchedules] = useState([]);
  const [schedTotal, setSchedTotal] = useState(0);
  const [schedPage, setSchedPage] = useUrlPage('spage', { zeroIndexed: true, defaultPage: 0 });
  const [schedPageSize, setSchedPageSize] = useUrlPageSize('svc_schedules', 25);
  const [loading, setLoading] = useState(true);
  // Each sub-page keeps its own independent device filter + search (seeded from the ?svc_device deep-link)
  const _svcDeviceSeed = new URLSearchParams(window.location.search).get('svc_device') || 'all';
  const [schedDevice, setSchedDevice] = useState(_svcDeviceSeed);
  const [histDevice, setHistDevice] = useState(_svcDeviceSeed);
  const [costDevice, setCostDevice] = useState(_svcDeviceSeed);
  const [statusFilter, setStatusFilter] = useState(_mtnMem.statusFilter || 'all');
  const [schedSearch, setSchedSearch] = useState(_mtnMem.schedSearch || '');
  const [histSearch, setHistSearch] = useState(_mtnMem.histSearch || '');
  const [costSearch, setCostSearch] = useState(_mtnMem.costSearch || '');
  // Debounced copies drive the fetches — one request per pause, not per keystroke
  const [schedSearchDebounced, setSchedSearchDebounced] = useState(_mtnMem.schedSearch || '');
  const [histSearchDebounced, setHistSearchDebounced] = useState(_mtnMem.histSearch || '');
  const [costSearchDebounced, setCostSearchDebounced] = useState(_mtnMem.costSearch || '');
  useEffect(() => { const t = setTimeout(() => setSchedSearchDebounced(schedSearch.trim()), 300); return () => clearTimeout(t); }, [schedSearch]);
  useEffect(() => { const t = setTimeout(() => setHistSearchDebounced(histSearch.trim()), 300); return () => clearTimeout(t); }, [histSearch]);
  useEffect(() => { const t = setTimeout(() => setCostSearchDebounced(costSearch.trim()), 300); return () => clearTimeout(t); }, [costSearch]);
  const schedReqRef = useRef(0);
  const histReqRef = useRef(0);
  const costReqRef = useRef(0);
  const loadedOnceRef = useRef({ sched: false, hist: false, cost: false });
  const [expanded, setExpanded] = useState(new Set());

  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [histPage, setHistPage] = useUrlPage('hpage', { zeroIndexed: true, defaultPage: 0 });
  const [histPageSize, setHistPageSize] = useUrlPageSize('svc_history', 25);
  const [histLoading, setHistLoading] = useState(false);
  const [histRange, setHistRange] = useState({ from: undefined, to: undefined });
  const [histDateResetKey, setHistDateResetKey] = useState(0);
  const [histSummary, setHistSummary] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [checkInterval, setCheckInterval] = useState(null);

  // Cost Report view
  const [costReport, setCostReport] = useState(null);
  const [costLoading, setCostLoading] = useState(false);
  const [costRange, setCostRange] = useState({ from: undefined, to: undefined });
  const [costDateResetKey, setCostDateResetKey] = useState(0);
  const [expandedCost, setExpandedCost] = useState(new Set());
  const [costPage, setCostPage] = useUrlPage('cpage', { zeroIndexed: true, defaultPage: 0 });
  const [costPageSize, setCostPageSize] = useUrlPageSize('svc_cost', 25);
  const [costTotal, setCostTotal] = useState(0);
  const [selectedCostIds, setSelectedCostIds] = useState([]);
  const [costSelectAllLoading, setCostSelectAllLoading] = useState(false);
  const [selectedHistIds, setSelectedHistIds] = useState([]);
  const [histSelectAllLoading, setHistSelectAllLoading] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [completeTarget, setCompleteTarget] = useState(null);
  const [completeForm, setCompleteForm] = useState({});
  const [completing, setCompleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingHistory, setEditingHistory] = useState(null);   // service_history row being edited (null = new log)
  const [attachmentsFor, setAttachmentsFor] = useState(null);   // history row whose attachments modal is open
  const [pendingDelete, setPendingDelete] = useState(null);     // { kind: 'schedule'|'history', item }
  const [duplicating, setDuplicating] = useState(null);         // schedule being duplicated to other assets
  const [editingLatestLog, setEditingLatestLog] = useState(null); // latest service log of the schedule being edited
  const [pendingRemoveAtt, setPendingRemoveAtt] = useState(null); // attachment awaiting removal confirm
  const [dupConfirm, setDupConfirm] = useState(null);           // 409 duplicate-log payload
  // Bulk Log Service — dedicated picker (choose task + vehicles) then per-vehicle readings/costs
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkStep, setBulkStep] = useState(1);              // 1 = select services, 2 = enter details
  const [bulkSchedules, setBulkSchedules] = useState([]);   // all open schedules for the picker
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkTaskFilter, setBulkTaskFilter] = useState('all');
  const [bulkSearch, setBulkSearch] = useState('');
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [bulkShared, setBulkShared] = useState({ completed_date: '', cost: '', vendor: '', performed_by: '', notes: '', attachments: [] });
  const [bulkRows, setBulkRows] = useState({});             // schedule_id -> { reading, cost }
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkDupConfirm, setBulkDupConfirm] = useState(null);   // rows skipped by the 24 h duplicate guard
  // Import / Export wizard
  const [importOpen, setImportOpen] = useState(false);
  const [lastImportRun, setLastImportRun] = useState(null);
  const [undoingImport, setUndoingImport] = useState(false);
  const [selectedSchedIds, setSelectedSchedIds] = useState([]);
  const [schedSelectAllLoading, setSchedSelectAllLoading] = useState(false);

  const [histSortKey, setHistSortKey] = useState(_mtnMem.histSortKey || 'completed_date');
  const [histSortDir, setHistSortDir] = useState(_mtnMem.histSortDir || 'desc');
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [, setTick] = useState(0);

  // Persist Maintenance search/filter/sort/sub-view for the session (restored on return).
  useEffect(() => {
    try {
      sessionStorage.setItem('maintenance_filters', JSON.stringify({
        view, schedSearch, histSearch, costSearch, statusFilter, histSortKey, histSortDir,
      }));
    } catch { /* ignore */ }
  }, [view, schedSearch, histSearch, costSearch, statusFilter, histSortKey, histSortDir]);

  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateInitialType, setTemplateInitialType] = useState('email');
  const setUrlParams = useSetUrlParams();

  // Consume the ?svc_device deep-link once: it seeds the initial device filter
  // (state above) but is immediately removed from the URL so a page refresh
  // starts with clean filters.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('svc_device')) {
      setUrlParams({ svc_device: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const devicesById = useMemo(() => Object.fromEntries(devices.map(d => [d.id, d])), [devices]);
  const tz = resolveTimezone();

  // datetime-local value (YYYY-MM-DDTHH:mm, user's timezone) → UTC ISO
  const localDateTimeToUtc = useCallback((v) => {
    if (!v) return undefined;
    const [d, t = '00:00'] = v.split('T');
    const [y, m, dd] = d.split('-').map(Number);
    const [hh, mm] = t.split(':').map(Number);
    const dt = zonedDateTimeToUtc({ year: y, month: m, day: dd, hour: hh || 0, minute: mm || 0, second: 0 }, tz);
    return dt ? dt.toISOString() : undefined;
  }, [tz]);

  const fetchDevices = useCallback(async () => {
    try {
      const res = await api.get('/fleet/my-devices?limit=2000');
      const d = res.data || {};
      setDevices([...(d.vehicles || []).map((x) => ({ ...x, _kind: 'vehicle' })), ...(d.trailers || []).map((x) => ({ ...x, _kind: 'trailer' })), ...(d.equipment || []).map((x) => ({ ...x, _kind: 'equipment' }))]);
    } catch { setDevices([]); }
  }, [api]);

  const fetchPresets = useCallback(async () => {
    try {
      const res = await api.get('/service/presets');
      setPresets(res.data.presets || []);
      setCheckInterval(res.data.check_interval_secs || null);
    } catch { /* ignore */ }
  }, [api]);

  const fetchTemplates = useCallback(async () => {
    try { const res = await api.get('/event-notification-templates'); setNotifTemplates(res.data.templates || []); } catch { /* ignore */ }
  }, [api]);

  const fetchSummary = useCallback(async () => {
    try {
      const p = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
      const [y, m] = p.split('-').map(Number);
      const dt = zonedDateTimeToUtc({ year: y, month: m, day: 1, hour: 0, minute: 0, second: 0 }, tz);
      const params = new URLSearchParams();
      if (dt) params.set('month_from', dt.toISOString().replace('Z', '+00:00'));
      const res = await api.get(`/service/summary?${params}`);
      setSummary(res.data);
    } catch { setSummary(null); }
  }, [api, tz]);

  const fetchSchedules = useCallback(async ({ silent = false } = {}) => {
    // Full spinner only on the very first load; afterwards rows stay on screen (dimmed) while new data loads
    const reqId = ++schedReqRef.current;
    if (!silent && !loadedOnceRef.current.sched) setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('paginated', 'true');
      params.set('skip', String(schedPage * schedPageSize));
      params.set('limit', String(schedPageSize));
      if (schedDevice !== 'all') params.set('device_id', schedDevice);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (schedSearchDebounced) params.set('search', schedSearchDebounced);
      const res = await api.get(`/service/schedules?${params}`);
      if (reqId !== schedReqRef.current) return;
      setSchedules(res.data?.items || []);
      setSchedTotal(res.data?.total || 0);
      setLastRefreshed(Date.now());
    } catch { if (reqId === schedReqRef.current) { setSchedules([]); setSchedTotal(0); } }
    finally { if (reqId === schedReqRef.current) { loadedOnceRef.current.sched = true; setLoading(false); } }
  }, [api, schedDevice, statusFilter, schedSearchDebounced, schedPage, schedPageSize]);

  const fetchHistory = useCallback(async () => {
    const reqId = ++histReqRef.current;
    if (!loadedOnceRef.current.hist) setHistLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('paginated', 'true');
      params.set('skip', String(histPage * histPageSize));
      params.set('limit', String(histPageSize));
      if (histDevice !== 'all') params.set('device_id', histDevice);
      if (histSearchDebounced) params.set('search', histSearchDebounced);
      if (histRange.from) params.set('date_from', histRange.from.toISOString());
      if (histRange.to) params.set('date_to', histRange.to.toISOString());
      params.set('sort_key', histSortKey);
      params.set('sort_dir', histSortDir);
      const res = await api.get(`/service/history?${params}`);
      if (reqId !== histReqRef.current) return;
      setHistory(res.data?.items || []);
      setHistoryTotal(res.data?.total || 0);
      setHistSummary(res.data?.summary || null);
    } catch { if (reqId === histReqRef.current) { setHistory([]); setHistoryTotal(0); setHistSummary(null); } }
    finally { if (reqId === histReqRef.current) { loadedOnceRef.current.hist = true; setHistLoading(false); } }
  }, [api, histPage, histPageSize, histDevice, histSearchDebounced, histRange, histSortKey, histSortDir]);

  const fetchCostReport = useCallback(async () => {
    const reqId = ++costReqRef.current;
    if (!loadedOnceRef.current.cost) setCostLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('paginated', 'true');
      params.set('skip', String(costPage * costPageSize));
      params.set('limit', String(costPageSize));
      if (costDevice !== 'all') params.set('device_id', costDevice);
      if (costRange.from) params.set('date_from', costRange.from.toISOString());
      if (costRange.to) params.set('date_to', costRange.to.toISOString());
      if (costSearchDebounced) params.set('search', costSearchDebounced);
      const res = await api.get(`/service/cost-report?${params}`);
      if (reqId !== costReqRef.current) return;
      setCostReport(res.data || null);
      setCostTotal(res.data?.total || 0);
    } catch { if (reqId === costReqRef.current) { setCostReport(null); setCostTotal(0); } }
    finally { if (reqId === costReqRef.current) { loadedOnceRef.current.cost = true; setCostLoading(false); } }
  }, [api, costDevice, costRange, costSearchDebounced, costPage, costPageSize]);

  // Clear selections when the underlying filters change
  useEffect(() => { setSelectedHistIds([]); }, [histDevice, histSearchDebounced, histRange]);
  useEffect(() => { setSelectedCostIds([]); }, [costDevice, costRange, costSearchDebounced]);
  useEffect(() => { setSelectedSchedIds([]); }, [schedDevice, statusFilter, schedSearchDebounced]);

  useEffect(() => { fetchDevices(); fetchPresets(); fetchTemplates(); }, [fetchDevices, fetchPresets, fetchTemplates]);
  useEffect(() => { if (view === 'schedules') fetchSchedules(); }, [view, fetchSchedules]);
  useEffect(() => { if (view === 'schedules') fetchSummary(); }, [view, fetchSummary]);
  useEffect(() => { if (view === 'history') fetchHistory(); }, [view, fetchHistory]);
  useEffect(() => { if (view === 'cost') fetchCostReport(); }, [view, fetchCostReport]);

  // Live values: refresh the Schedules list every 5 min while visible, and when the window regains focus.
  useEffect(() => {
    if (view !== 'schedules') return undefined;
    // silent: keep the current rows on screen while new data loads (no flashing / disappearing table)
    const refresh = () => { if (document.visibilityState === 'visible') { fetchSchedules({ silent: true }); fetchSummary(); } };
    const poll = setInterval(refresh, 300000);
    const tick = setInterval(() => setTick((t) => t + 1), 5000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { clearInterval(poll); clearInterval(tick); window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [view, fetchSchedules, fetchSummary]);

  // ── Form helpers ──
  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const updateStage = (idx, patch) => setForm(prev => {
    const stages = [...prev.reminder_stages];
    stages[idx] = { ...stages[idx], ...patch };
    return { ...prev, reminder_stages: stages };
  });
  const updateStageNotif = (idx, key, val) => setForm(prev => {
    const stages = [...prev.reminder_stages];
    stages[idx] = { ...stages[idx], notifications: { ...stages[idx].notifications, [key]: val } };
    return { ...prev, reminder_stages: stages };
  });
  const updateStageAction = (idx, key, val) => setForm(prev => {
    const stages = [...prev.reminder_stages];
    stages[idx] = { ...stages[idx], actions: { ...stages[idx].actions, [key]: val } };
    return { ...prev, reminder_stages: stages };
  });
  const updateStageCommand = (idx, key, val) => setForm(prev => {
    const stages = [...prev.reminder_stages];
    const cmd = { ...(stages[idx].actions.command_on_trigger || {}), [key]: val };
    stages[idx] = { ...stages[idx], actions: { ...stages[idx].actions, command_on_trigger: cmd } };
    return { ...prev, reminder_stages: stages };
  });

  const openCreate = () => { setEditingId(null); setForm(emptyForm()); setEditorOpen(true); };
  const openEdit = (s) => {
    setEditingId(s.id);
    setEditingLatestLog(s.latest_log || null);
    setForm({
      device_id: s.device_id, task_name: s.task_name || '', task_preset: s.task_preset || 'service',
      basis: s.basis, interval_value: s.interval_value ?? '', interval_days: s.interval_days ?? '', recurring: !!s.recurring, is_active: !!s.is_active,
      fixed_schedule: s.fixed_schedule ?? false,
      baseline_value: s.baseline_value ?? '', baseline_date: s.baseline_date ? s.baseline_date.slice(0, 10) : '',
      notes: s.notes || '',
      attachments: [...(s.attachments || [])],
      reminder_stages: (s.reminder_stages && s.reminder_stages.length ? s.reminder_stages : [makeStage()]).map(st => ({
        ...makeStage(), ...st,
        label: st.label ?? '',
        trigger_days: st.trigger_days ?? 0,
        raise_alert: st.raise_alert !== false,
        notifications: { ...DEFAULT_STAGE_NOTIF, ...(st.notifications || {}) },
        actions: { ...DEFAULT_STAGE_ACTIONS, ...(st.actions || {}), command_on_trigger: { ...DEFAULT_STAGE_ACTIONS.command_on_trigger, ...((st.actions || {}).command_on_trigger || {}) } },
      })),
    });
    setEditorOpen(true);
  };

  const saveSchedule = async () => {
    if (!form.device_id) { toast.error('Select a device'); return; }
    if (!form.task_name.trim()) { toast.error('Enter a task name'); return; }
    if (!form.interval_value || Number(form.interval_value) <= 0) { toast.error('Enter a valid interval'); return; }
    if (isDualBasis(form.basis) && (!form.interval_days || Number(form.interval_days) <= 0)) { toast.error('Enter a valid calendar interval (days)'); return; }
    if (form.basis !== 'calendar' && (form.baseline_value === '' || form.baseline_value === null)) { toast.error(`Enter the Last Serviced ${usesOdometer(form.basis) ? 'odometer (km)' : 'engine hours'}`); return; }
    if ((form.basis === 'calendar' || isDualBasis(form.basis)) && !form.baseline_date) { toast.error('Enter the Last Serviced Date'); return; }
    setSaving(true);
    try {
      const payload = {
        device_id: form.device_id, task_name: form.task_name.trim(), task_preset: form.task_preset,
        basis: form.basis, interval_value: Number(form.interval_value), recurring: form.recurring, is_active: form.is_active,
        fixed_schedule: form.basis === 'calendar' ? false : !!form.fixed_schedule,
        notes: form.notes || null,
        attachments: form.attachments || [],
        reminder_stages: form.reminder_stages.map(st => ({ ...st, trigger_value: Number(st.trigger_value) || 0, trigger_days: Number(st.trigger_days) || 0 })),
      };
      if (isDualBasis(form.basis)) payload.interval_days = Number(form.interval_days);
      if (form.basis !== 'calendar' && form.baseline_value !== '' && form.baseline_value !== null) {
        payload.baseline_value = Number(form.baseline_value);
      }
      if ((form.basis === 'calendar' || isDualBasis(form.basis)) && form.baseline_date) {
        payload.baseline_date = new Date(form.baseline_date).toISOString();
      }
      if (editingId) {
        await api.put(`/service/schedules/${editingId}`, payload);
        toast.success('Schedule updated');
      } else {
        await api.post('/service/schedules', payload);
        toast.success('Schedule created');
      }
      setEditorOpen(false);
      fetchSchedules();
      fetchSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save schedule');
    } finally { setSaving(false); }
  };

  const deleteSchedule = (s) => setPendingDelete({ kind: 'schedule', item: s });
  const deleteHistoryRow = (h) => setPendingDelete({ kind: 'history', item: h });
  const confirmDelete = async () => {
    const { kind, item } = pendingDelete || {};
    if (!item) return;
    try {
      if (kind === 'schedule') {
        await api.delete(`/service/schedules/${item.id}`);
        toast.success('Schedule deleted'); fetchSchedules(); fetchSummary();
      } else {
        const r = await api.delete(`/service/history/${item.id}`);
        toast.success(r.data?.attachments_removed ? `Service log deleted (${r.data.attachments_removed} file(s) removed from storage)` : 'Service log deleted');
        fetchHistory(); fetchSummary();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete'); }
  };

  // YYYY-MM-DDTHH:mm of an instant in the USER's timezone (Settings) — never the browser's.
  const localDateInput = (iso) => {
    const p = getZonedParts(iso || new Date(), { timezone: tz });
    if (!p) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour ?? 0)}:${pad(p.minute ?? 0)}`;
  };

  // New log: odometer / engine hours are deliberately NOT pre-filled from live telemetry —
  // the user must enter the values as they were when the service was actually done.
  const openComplete = (s) => {
    setEditingHistory(null);
    setCompleteTarget(s);
    setCompleteForm({
      completed_date: localDateInput(),
      odometer_at_service: '', engine_hours_at_service: '',
      cost: '', vendor: '', performed_by: '', notes: '', attachments: [], next_due_override: '',
    });
  };

  const openEditHistory = (h) => {
    setEditingHistory(h);
    setCompleteTarget({ id: h.schedule_id, task_name: h.task_name, basis: h.basis, device_id: h.device_id, asset_name: h.asset_name, device_name: h.device_name });
    setCompleteForm({
      completed_date: localDateInput(h.completed_date),
      odometer_at_service: h.odometer_at_service ?? '', engine_hours_at_service: h.engine_hours_at_service ?? '',
      cost: h.cost ?? '', vendor: h.vendor || '', performed_by: h.performed_by || '', notes: h.notes || '',
      attachments: [...(h.attachments || [])],
    });
  };

  const uploadAttachment = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post('/service/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const { id, url, name, size, content_type } = res.data;
        setCompleteForm(prev => ({ ...prev, attachments: [...(prev.attachments || []), { id, url, name, size, content_type }] }));
        ok += 1;
      } catch (e) {
        const status = e?.response?.status;
        toast.error(`${file.name}: ${e?.response?.data?.detail || `Upload failed${status ? ` (HTTP ${status})` : ''}`}`);
      }
    }
    if (ok) toast.success(ok === 1 ? 'Attachment uploaded' : `${ok} attachments uploaded`);
    setUploading(false);
  };

  const removeAttachment = async (att) => {
    try {
      if (att?.id) await api.delete(`/service/attachments/${att.id}`);
      setCompleteForm(prev => ({ ...prev, attachments: (prev.attachments || []).filter((a) => (a.id || a.url) !== (att.id || att.url)) }));
      if (editingHistory) fetchHistory();
      toast.success('Attachment removed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to remove attachment'); }
  };

  // ── Import / Export ──
  const fetchLatestImportRun = useCallback(async () => {
    try { const res = await api.get('/service/schedules/import-runs/latest'); setLastImportRun(res.data?.run || null); }
    catch { setLastImportRun(null); }
  }, [api]);
  useEffect(() => { if (view === 'schedules') fetchLatestImportRun(); }, [view, fetchLatestImportRun]);

  const handleExportSchedules = async (scope, format) => {
    try {
      let list;
      if (scope === 'selected') list = schedules.filter((s) => selectedSchedIds.includes(s.id));
      else if (scope === 'filtered') list = schedules;
      else { const res = await api.get('/service/schedules'); list = Array.isArray(res.data) ? res.data : (res.data?.items || []); }
      if (!list.length) { toast.error('No schedules to export'); return; }
      exportSchedules(list, devicesById, format, userDateFormat);
      toast.success(`Exported ${list.length} schedule(s)`);
    } catch { toast.error('Export failed'); }
  };

  const undoImport = async () => {
    if (!lastImportRun || undoingImport) return;
    setUndoingImport(true);
    try {
      const res = await api.post(`/service/schedules/import-runs/${lastImportRun.id}/undo`);
      toast.success(res.data?.message || 'Import undone');
      setLastImportRun(null); fetchSchedules(); fetchSummary();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Undo failed'); }
    finally { setUndoingImport(false); }
  };

  // ── Bulk Log Service ──
  const openBulk = async () => {
    setBulkStep(1); setBulkTaskFilter('all'); setBulkSearch(''); setBulkSelectedIds([]);
    setBulkShared({ completed_date: localDateInput(), cost: '', vendor: '', performed_by: '', notes: '', attachments: [] });
    setBulkRows({}); setBulkOpen(true); setBulkLoading(true);
    try {
      const res = await api.get('/service/schedules');
      const open = (Array.isArray(res.data) ? res.data : (res.data?.items || [])).filter((s) => s.is_active && !s.closed);
      setBulkSchedules(open);
    } catch { toast.error('Failed to load schedules'); setBulkSchedules([]); }
    finally { setBulkLoading(false); }
  };

  const bulkToggle = (id) => setBulkSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const bulkGoDetails = () => {
    if (!bulkSelectedIds.length) { toast.error('Select at least one vehicle'); return; }
    // Pre-fill each row's reading with the vehicle's current live reading (editable)
    setBulkRows((prev) => {
      const next = { ...prev };
      bulkSelectedIds.forEach((id) => {
        const s = bulkSchedules.find((x) => x.id === id);
        if (!next[id]) next[id] = { reading: (s && s.current_value != null && (usesOdometer(s.basis) || usesEngineHours(s.basis))) ? String(s.current_value) : '', cost: '' };
      });
      return next;
    });
    setBulkStep(2);
  };

  const bulkUpload = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setBulkUploading(true);
    let ok = 0;
    for (const file of list) {
      try {
        const fd = new FormData(); fd.append('file', file);
        const res = await api.post('/service/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const { id, url, name, size, content_type } = res.data;
        setBulkShared((prev) => ({ ...prev, attachments: [...(prev.attachments || []), { id, url, name, size, content_type }] }));
        ok += 1;
      } catch (e) { toast.error(`${file.name}: ${e?.response?.data?.detail || 'Upload failed'}`); }
    }
    if (ok) toast.success(ok === 1 ? 'Receipt attached' : `${ok} files attached`);
    setBulkUploading(false);
  };

  const bulkApplyCostToAll = () => {
    if (bulkShared.cost === '') { toast.error('Enter a cost to apply'); return; }
    setBulkRows((prev) => { const n = { ...prev }; bulkSelectedIds.forEach((id) => { n[id] = { ...(n[id] || {}), cost: bulkShared.cost }; }); return n; });
    toast.success('Cost applied to all rows');
  };

  const submitBulk = async (force = false) => {
    // Validate required per-vehicle readings up front
    for (const id of bulkSelectedIds) {
      const s = bulkSchedules.find((x) => x.id === id);
      if (!s) continue;
      const row = bulkRows[id] || {};
      if (s.recurring && usesOdometer(s.basis) && (row.reading === '' || row.reading == null)) { toast.error(`Enter the odometer for ${deviceLabel(s)}`); return; }
      if (s.recurring && usesEngineHours(s.basis) && (row.reading === '' || row.reading == null)) { toast.error(`Enter the engine hours for ${deviceLabel(s)}`); return; }
    }
    setBulkSubmitting(true);
    try {
      const items = bulkSelectedIds.map((id) => {
        const s = bulkSchedules.find((x) => x.id === id) || {};
        const row = bulkRows[id] || {};
        return {
          schedule_id: id,
          odometer_at_service: usesOdometer(s.basis) && row.reading !== '' ? Number(row.reading) : undefined,
          engine_hours_at_service: usesEngineHours(s.basis) && row.reading !== '' ? Number(row.reading) : undefined,
          cost: row.cost !== '' && row.cost != null ? Number(row.cost) : undefined,
        };
      });
      const body = {
        items,
        completed_date: localDateTimeToUtc(bulkShared.completed_date),
        cost: bulkShared.cost !== '' ? Number(bulkShared.cost) : undefined,
        vendor: bulkShared.vendor || undefined,
        performed_by: bulkShared.performed_by || undefined,
        notes: bulkShared.notes || undefined,
        attachments: bulkShared.attachments || [],
        force,
      };
      const res = await api.post('/service/schedules/bulk-complete', body);
      const { succeeded, failed, results } = res.data;
      const dupFails = (results || []).filter((r) => !r.ok && r.code === 'duplicate_recent_log');
      if (succeeded > 0) toast.success(`Logged ${succeeded} service${succeeded === 1 ? '' : 's'}${failed ? `, ${failed} skipped` : ''}`);
      if (failed > 0 && succeeded === 0) toast.error(`No services logged — ${failed} skipped`);
      // Some rows hit the 24 h duplicate guard — ask for explicit confirmation (only then offer "Log anyway")
      if (dupFails.length && !force) {
        setBulkSelectedIds(dupFails.map((r) => r.schedule_id));
        setBulkDupConfirm(dupFails);
        setBulkSubmitting(false);
        if (succeeded > 0) { fetchSchedules(); fetchSummary(); }
        return;
      }
      setBulkDupConfirm(null);
      setBulkOpen(false);
      fetchSchedules(); fetchSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Bulk log failed');
    } finally { setBulkSubmitting(false); }
  };


  // Schedule documents (specs / checklists / manuals) — same storage as service-log attachments
  const uploadScheduleDoc = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post('/service/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const { id, url, name, size, content_type } = res.data;
        setForm(prev => ({ ...prev, attachments: [...(prev.attachments || []), { id, url, name, size, content_type }] }));
        ok += 1;
      } catch (e) {
        toast.error(`${file.name}: ${e?.response?.data?.detail || 'Upload failed'}`);
      }
    }
    if (ok) toast.success(ok === 1 ? 'Document uploaded' : `${ok} documents uploaded`);
    setUploading(false);
  };

  const removeScheduleDoc = async (att) => {
    try {
      if (att?.id) await api.delete(`/service/attachments/${att.id}`);
      setForm(prev => ({ ...prev, attachments: (prev.attachments || []).filter((a) => (a.id || a.url) !== (att.id || att.url)) }));
      if (editingId) fetchSchedules({ silent: true });
      toast.success('Document removed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to remove document'); }
  };

  // Cancelling a NEW schedule: discard documents uploaded in this session so nothing is orphaned in storage.
  const closeEditor = async () => {
    const orphans = !editingId ? (form.attachments || []).filter((a) => a.id) : [];
    setEditorOpen(false);
    if (orphans.length) {
      await Promise.allSettled(orphans.map((a) => api.delete(`/service/attachments/${a.id}`)));
      toast.info(`${orphans.length} unsaved document(s) discarded`);
    }
  };

  // Cancelling a NEW log: files were already uploaded — delete them so nothing is orphaned in storage.
  const closeCompleteDialog = async () => {
    const orphans = !editingHistory ? (completeForm.attachments || []).filter((a) => a.id) : [];
    setCompleteTarget(null);
    setEditingHistory(null);
    if (orphans.length) {
      await Promise.allSettled(orphans.map((a) => api.delete(`/service/attachments/${a.id}`)));
      toast.info(`${orphans.length} unsaved file(s) discarded`);
    }
  };

  // Open an attachment in a new tab. The tab is opened synchronously (inside the click) and the
  // authenticated blob is streamed into it afterwards — avoids popup blockers.
  const openAttachment = async (att) => {
    const win = window.open('', '_blank');
    try {
      const r = await api.get(att.url.replace(/^\/api/, ''), { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(r.data);
      if (win) win.location = blobUrl; else window.open(blobUrl, '_blank');
    } catch (e) {
      if (win) win.close();
      toast.error(e?.response?.status === 404 ? 'Attachment file not found' : 'Could not open attachment');
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const r = await api.get(att.url.replace(/^\/api/, ''), { responseType: 'blob' });
      downloadBlob(r.data, att.name || 'attachment');
    } catch { toast.error('Could not download attachment'); }
  };

  const fmtSize = (n) => (n == null ? '' : n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

  const submitComplete = async (force = false) => {
    if (!completeTarget) return;
    const basis = completeTarget.basis;
    if (usesOdometer(basis) && completeForm.odometer_at_service === '') { toast.error('Enter the odometer when serviced'); return; }
    if (usesEngineHours(basis) && completeForm.engine_hours_at_service === '') { toast.error('Enter the engine hours when serviced'); return; }
    setCompleting(true);
    try {
      const body = {
        completed_date: localDateTimeToUtc(completeForm.completed_date),
        odometer_at_service: usesOdometer(basis) && completeForm.odometer_at_service !== '' ? Number(completeForm.odometer_at_service) : undefined,
        engine_hours_at_service: usesEngineHours(basis) && completeForm.engine_hours_at_service !== '' ? Number(completeForm.engine_hours_at_service) : undefined,
        cost: completeForm.cost !== '' ? Number(completeForm.cost) : undefined,
        vendor: completeForm.vendor || undefined, performed_by: completeForm.performed_by || undefined,
        notes: completeForm.notes || undefined,
        attachments: completeForm.attachments || [],
      };
      if (editingHistory) {
        const r = await api.put(`/service/history/${editingHistory.id}`, { ...body, notes: completeForm.notes || '', vendor: completeForm.vendor || '', performed_by: completeForm.performed_by || '', cost: completeForm.cost !== '' ? Number(completeForm.cost) : null });
        toast.success(r.data?.rebaselined ? 'Service log updated — schedule due date recalculated' : 'Service log updated');
        fetchHistory();
      } else {
        const overrideVal = completeForm.next_due_override !== '' && completeForm.next_due_override != null ? Number(completeForm.next_due_override) : undefined;
        await api.post(`/service/schedules/${completeTarget.id}/complete`, { ...body, next_due_override: overrideVal, force });
        toast.success('Service logged');
      }
      setCompleteTarget(null);
      setEditingHistory(null);
      fetchSchedules();
      fetchSummary();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === 'duplicate_recent_log') { setDupConfirm(detail); return; }
      toast.error((typeof detail === 'string' && detail) || (editingHistory ? 'Failed to update service log' : 'Failed to log service'));
    }
    finally { setCompleting(false); }
  };

  const runCheckNow = async () => {
    try {
      const res = await api.post('/service/run-check');
      toast.success(`Checked ${res.data.schedules_checked} schedule(s), ${res.data.alerts_raised_or_escalated} alert(s) raised`);
      fetchSchedules();
      fetchSummary();
      window.dispatchEvent(new CustomEvent('ensue:alerts-changed'));
    } catch { toast.error('Check failed'); }
  };

  const openTemplateManager = (type) => { setTemplateInitialType(type || 'email'); setTemplateModalOpen(true); };

  const unitFor = (basis) => BASIS_META[basis]?.unit || '';
  const fmtRem = (rem, u) => (rem < 0 ? `${Math.abs(rem)} ${u} overdue` : `${rem} ${u} left`);
  const remainingLabel = (s) => {
    if (isDualBasis(s.basis)) {
      const parts = [];
      if (s.remaining != null) parts.push(fmtRem(s.remaining, unitFor(s.basis)));
      if (s.remaining_days != null) parts.push(fmtRem(s.remaining_days, 'days'));
      if (!parts.length) return '—';
      const first = s.due_first === 'calendar' ? 'date first' : s.due_first === 'metric' ? `${unitFor(s.basis)} first` : null;
      return parts.join(' or ') + (first ? ` — ${first}` : '');
    }
    if (s.remaining == null) return '—';
    return fmtRem(s.remaining, unitFor(s.basis));
  };
  const dueLabel = (s) => {
    if (s.basis === 'calendar') return s.next_due_date ? formatDateOnly(s.next_due_date, { timezone: tz }) : '—';
    if (isDualBasis(s.basis)) {
      const v = s.next_due_value != null ? `${s.next_due_value} ${unitFor(s.basis)}` : '—';
      const d = s.next_due_date ? formatDateOnly(s.next_due_date, { timezone: tz }) : '—';
      return `${v} or ${d}`;
    }
    return s.next_due_value != null ? `${s.next_due_value} ${unitFor(s.basis)}` : '—';
  };
  const intervalText = (s) => isDualBasis(s.basis)
    ? `${s.interval_value} ${unitFor(s.basis)} or ${s.interval_days} days`
    : `${s.interval_value} ${unitFor(s.basis)}`;

  // Predicted due date (km / hour schedules) from recent daily usage
  const predictedInfo = (s) => {
    if (!BASIS_META[s.basis]?.unit || s.basis === 'calendar' || !s.is_active) return null;
    if ((s.remaining != null && s.remaining < 0) || (s.remaining_days != null && s.remaining_days < 0)) return null;
    const u = unitFor(s.basis);
    const rateTxt = s.usage_per_day != null
      ? `~${s.usage_per_day} ${u}/day ${s.usage_source === 'snapshots' ? `over the last ${s.usage_days} day${s.usage_days === 1 ? '' : 's'}` : `since the last logged service (${s.usage_days} days)`}`
      : null;
    if (s.predicted_due_date) {
      const d = formatDateOnly(s.predicted_due_date, { timezone: tz });
      if (s.predicted_side === 'calendar') return { text: `est. ${d}`, title: `Calendar due date comes first${rateTxt ? ` — ${rateTxt}` : ''}` };
      return { text: `est. ${d}`, title: `Estimated from recent usage: ${rateTxt}` };
    }
    if (s.usage_per_day === 0) return { text: 'no recent usage', title: `No ${u} recorded ${s.usage_source === 'snapshots' ? `in the last ${s.usage_days} days` : 'since the last logged service'}` };
    if (s.current_value == null || s.basis_source_off) return null;
    return { text: 'collecting usage data', title: 'A daily meter snapshot is recorded — an estimated due date appears once at least one day of usage is available' };
  };

  const deviceLabel = (s) => s.asset_name || s.device_name || (devicesById[s.device_id] ? deviceDisplayName(devicesById[s.device_id]) : 'Device');
  const editStageDeviceIds = form.device_id ? [form.device_id] : [];

  const selDev = devicesById[form.device_id];
  const odoOff = selDev ? !['gps', 'sensor'].includes(selDev.odometer_source) : false;
  const ehOff = selDev ? !['ignition', 'sensor'].includes(selDev.engine_hours_source) : false;
  const currentReading = selDev && usesOdometer(form.basis) ? selDev.computed_odometer
    : selDev && usesEngineHours(form.basis) ? selDev.computed_engine_hours : null;
  const sourceLabel = usesOdometer(form.basis)
    ? ({ gps: 'GPS', sensor: 'Sensor' }[selDev?.odometer_source] || 'Off')
    : ({ ignition: 'Ignition', sensor: 'Sensor' }[selDev?.engine_hours_source] || 'Off');
  const intervalLabel = checkInterval
    ? (checkInterval >= 3600 ? `${Math.round(checkInterval / 3600)} hour${checkInterval >= 7200 ? 's' : ''}` : `${Math.round(checkInterval / 60)} min`)
    : null;

  const exportHistory = async (fmt) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (histDevice !== 'all') params.set('device_id', histDevice);
      if (histSearch) params.set('search', histSearch);
      if (histRange.from) params.set('date_from', histRange.from.toISOString());
      if (histRange.to) params.set('date_to', histRange.to.toISOString());
      params.set('limit', '10000');
      const res = await api.get(`/service/history?${params}`);
      let rows = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      if (selectedHistIds.length) rows = rows.filter(r => selectedHistIds.includes(r.id));
      if (!rows.length) { toast.info('No records to export'); return; }
      const headers = ['Date', 'Vehicle', 'Task', 'Odometer (km)', 'Engine Hours', 'Cost', 'Vendor', 'Performed By', 'Notes'];
      const data = rows.map(h => [
        formatDT(h.completed_date, { timezone: tz, seconds: false }),
        h.asset_name || h.device_name || '',
        h.task_name || '',
        h.odometer_at_service != null ? String(h.odometer_at_service) : '',
        h.engine_hours_at_service != null ? String(h.engine_hours_at_service) : '',
        h.cost != null ? Number(h.cost).toFixed(2) : '',
        h.vendor || '', h.performed_by || '', h.notes || '',
      ]);
      const fname = `service_history_${getFileTimestamp()}`;
      const parts = [`${rows.length} record${rows.length === 1 ? '' : 's'}${selectedHistIds.length ? ' (selected)' : ''}`];
      if (histDevice !== 'all' && devicesById[histDevice]) parts.push(deviceDisplayName(devicesById[histDevice]));
      if (histRange.from || histRange.to) parts.push(`${histRange.from ? formatDT(histRange.from, { timezone: resolveTimezone(), seconds: false }) : '…'} – ${histRange.to ? formatDT(histRange.to, { timezone: resolveTimezone(), seconds: false }) : '…'}`);
      const subtitle = parts.join(' • ');
      if (fmt === 'csv') {
        downloadBlob(rowsToCsv([headers, ...data]), `${fname}.csv`, 'text/csv;charset=utf-8;');
        toast.success('CSV exported');
      } else if (fmt === 'html') {
        await exportTableHtml('Service History', subtitle, headers, data, `${fname}.html`);
        toast.success('HTML exported');
      } else {
        const logo = await loadLogoPng();
        const { doc, layout } = newLandscapeDoc();
        const weights = [10, 14, 14, 8, 8, 7, 10, 10, 19];
        const y = addBrandHeader(doc, layout, logo, 'Service History', subtitle);
        drawPdfTable(doc, layout, y, headers.map((label, i) => ({ label, weight: weights[i] })), data);
        addFooters(doc, layout);
        doc.save(`${fname}.pdf`);
        toast.success('PDF exported');
      }
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const exportCostReport = async (fmt) => {
    setExporting(true);
    try {
      // Always fetch the FULL (unpaginated) report so exports cover every vehicle / selection across pages
      const params = new URLSearchParams();
      if (costDevice !== 'all') params.set('device_id', costDevice);
      if (costRange.from) params.set('date_from', costRange.from.toISOString());
      if (costRange.to) params.set('date_to', costRange.to.toISOString());
      if (costSearchDebounced) params.set('search', costSearchDebounced);
      const res = await api.get(`/service/cost-report?${params}`);
      let vehicles = res.data?.vehicles || [];
      if (selectedCostIds.length) vehicles = vehicles.filter((v) => selectedCostIds.includes(v.device_id));
      if (!vehicles.length) { toast.info('No records to export'); return; }
      // Per-vehicle totals row followed by one row per individual service
      const headers = ['Vehicle', 'Serviced Date', 'Task', 'Cost'];
      const data = [];
      let grandSpend = 0, grandServices = 0;
      vehicles.forEach((v) => {
        grandSpend += Number(v.total_spend || 0);
        grandServices += Number(v.service_count || 0);
        data.push([v.label, '', `— All services (${v.service_count}) —`, Number(v.total_spend).toFixed(2)]);
        (v.services || []).forEach((s) => {
          data.push(['', formatDT(s.completed_date, { timezone: tz, seconds: false }), s.task || '', Number(s.cost || 0).toFixed(2)]);
        });
      });
      const fname = `service_cost_report_${getFileTimestamp()}`;
      const parts = [`${vehicles.length} vehicle${vehicles.length === 1 ? '' : 's'}${selectedCostIds.length ? ' (selected)' : ''}`, `Total ${money(grandSpend)}`, `${grandServices} service${grandServices === 1 ? '' : 's'}`];
      if (costDevice !== 'all' && devicesById[costDevice]) parts.push(deviceDisplayName(devicesById[costDevice]));
      parts.push((costRange.from || costRange.to) ? `${costRange.from ? formatDT(costRange.from, { timezone: tz, seconds: false }) : '…'} – ${costRange.to ? formatDT(costRange.to, { timezone: tz, seconds: false }) : '…'}` : 'All time');
      const subtitle = parts.join(' • ');
      if (fmt === 'csv') {
        downloadBlob(rowsToCsv([headers, ...data]), `${fname}.csv`, 'text/csv;charset=utf-8;');
        toast.success('CSV exported');
      } else if (fmt === 'html') {
        await exportTableHtml('Service Cost Report', subtitle, headers, data, `${fname}.html`);
        toast.success('HTML exported');
      } else {
        const logo = await loadLogoPng();
        const { doc, layout } = newLandscapeDoc();
        const weights = [30, 22, 30, 18];
        const y = addBrandHeader(doc, layout, logo, 'Service Cost Report', subtitle);
        drawPdfTable(doc, layout, y, headers.map((label, i) => ({ label, weight: weights[i] })), data);
        addFooters(doc, layout);
        doc.save(`${fname}.pdf`);
        toast.success('PDF exported');
      }
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  // Toolbar actions — placed on each sub-page's filter row (consistent with Alerts / Events pages).
  const runCheckBtn = (
    <Button variant="outline" size="sm" onClick={runCheckNow} data-testid="service-run-check-btn">
      <RefreshCw className="w-4 h-4 mr-1" /> Run Check
    </Button>
  );
  const scheduleActions = (
    <>
      <Button variant="outline" size="sm" onClick={openBulk} data-testid="service-bulk-log-btn">
        <CheckCircle className="w-4 h-4 mr-1" /> Bulk Log
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" data-testid="service-import-dropdown-trigger">
            <Download className="w-4 h-4 mr-1" /> Import <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-70" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs">Import options</DropdownMenuLabel>
          <DropdownMenuItem onSelect={() => setImportOpen(true)} data-testid="service-import-open-wizard">
            <Sparkles className="w-4 h-4 mr-2 text-[#2563eb]" />
            <div className="flex-1 min-w-0"><p className="text-sm font-medium leading-tight">Import Wizard</p><p className="text-[11px] text-muted-foreground leading-tight">Preview, edit &amp; validate before import</p></div>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] text-muted-foreground pt-1 pb-0.5">Templates</DropdownMenuLabel>
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); downloadScheduleTemplateXlsx(userDateFormat); }} data-testid="service-import-template-xlsx">
            <FileSpreadsheet className="w-4 h-4 mr-2 text-[#2563eb]" /> Excel-safe template (.xlsx)
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); downloadScheduleTemplateCsv(userDateFormat); }} data-testid="service-import-template-csv">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> CSV template (.csv)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={selectedSchedIds.length === 0} data-testid="service-export-dropdown-trigger">
            <Upload className="w-4 h-4 mr-1" /> Export{selectedSchedIds.length ? ` (${selectedSchedIds.length})` : ''} <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-70" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-[10px] text-muted-foreground pt-1 pb-0.5">Selected ({selectedSchedIds.length})</DropdownMenuLabel>
          <DropdownMenuItem onSelect={() => handleExportSchedules('selected', 'xlsx')} data-testid="service-export-selected-xlsx"><FileSpreadsheet className="w-4 h-4 mr-2 text-[#2563eb]" /> Selected → .xlsx</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => handleExportSchedules('selected', 'csv')} data-testid="service-export-selected-csv"><FileSpreadsheet className="w-4 h-4 mr-2" /> Selected → .csv</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button size="sm" onClick={openCreate} data-testid="service-add-schedule-btn">
        <Plus className="w-4 h-4 mr-1" /> Add Schedule
      </Button>
    </>
  );

  return (
    <div className="space-y-4" data-testid="maintenance-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-foreground" />
            <h2 className="text-xl font-semibold">Maintenance</h2>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Schedule services by odometer, engine hours or date. Reminder stages raise Service alerts and send their own notifications.
          </p>
        </div>
      </div>

      {/* View toggle — same separate equal-width buttons as Events → Event Log / Event Rules */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={view === 'schedules' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setView('schedules')}
          className={`gap-2 min-w-[170px] justify-center ${view === 'schedules' ? 'bg-[#2563eb] hover:bg-[#1d4ed8]' : ''}`}
          data-testid="service-view-schedules"
        >
          <CalIcon className="w-4 h-4 mr-1" />
          Schedules
        </Button>
        <Button
          variant={view === 'history' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setView('history')}
          className={`gap-2 min-w-[170px] justify-center ${view === 'history' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
          data-testid="service-view-history"
        >
          <History className="w-4 h-4 mr-1" />
          Service History
        </Button>
        <Button
          variant={view === 'cost' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setView('cost')}
          className={`gap-2 min-w-[170px] justify-center ${view === 'cost' ? 'bg-orange-600 hover:bg-orange-700' : ''}`}
          data-testid="service-view-cost"
        >
          <DollarSign className="w-4 h-4 mr-1" />
          Cost Report
        </Button>
      </div>

      {/* Summary stat cards (schedules view) — styled to match Alerts / Event Log cards */}
      {view === 'schedules' && summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="service-summary-cards">
          {[
            ['overdue', 'Overdue', AlertTriangle, 'bg-red-100 dark:bg-red-900/30', 'text-red-600'],
            ['due_soon', 'Due Soon', Clock, 'bg-amber-100 dark:bg-amber-900/30', 'text-amber-600'],
            ['ok', 'OK', CheckCircle, 'bg-green-100 dark:bg-green-900/30', 'text-green-600'],
          ].map(([key, label, Icon, iconBg, iconColor]) => (
            <button key={key} type="button" className="text-left w-full h-full" onClick={() => { setStatusFilter(statusFilter === key ? 'all' : key); setSchedPage(0); }} data-testid={`service-stat-${key}`}>
              <Card className={`h-full transition-shadow hover:shadow-md ${statusFilter === key ? 'ring-2 ring-[#2563eb]' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{label}</p>
                      <p className="text-2xl font-bold">{summary.counts?.[key] ?? 0}</p>
                    </div>
                    <div className={`w-10 h-10 rounded-full ${iconBg} flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${iconColor}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </button>
          ))}
          <Card data-testid="service-stat-month-spend">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Spend This Month</p>
                  <p className="text-2xl font-bold">
                    ${Number(summary.month_spend || 0).toFixed(2)}
                    <span className="text-xs font-normal text-muted-foreground ml-1">({summary.month_services} service{summary.month_services === 1 ? '' : 's'})</span>
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {view === 'history' && histSummary && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="service-history-summary">
          <Card><CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Services</p>
                <p className="text-2xl font-bold" data-testid="service-summary-count">{histSummary.count}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-slate-600" />
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Spend</p>
                <p className="text-2xl font-bold" data-testid="service-summary-total">${Number(histSummary.total_cost || 0).toFixed(2)}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Cost</p>
                <p className="text-2xl font-bold" data-testid="service-summary-avg">{histSummary.avg_cost != null ? `$${Number(histSummary.avg_cost).toFixed(2)}` : '—'}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Gauge className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent></Card>
        </div>
      )}

      {view === 'cost' && costReport?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="service-cost-summary">
          <Card><CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Spend</p>
                <p className="text-2xl font-bold" data-testid="service-cost-total">{money(costReport.summary.total_spend)}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Services</p>
                <p className="text-2xl font-bold" data-testid="service-cost-services">{costReport.summary.service_count}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-slate-600" />
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Vehicles</p>
                <p className="text-2xl font-bold" data-testid="service-cost-vehicles">{costReport.summary.vehicle_count}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg / Service</p>
                <p className="text-2xl font-bold" data-testid="service-cost-avg">{costReport.summary.avg_cost != null ? money(costReport.summary.avg_cost) : '—'}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Gauge className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent></Card>
        </div>
      )}
      {/* Filters — each sub-page keeps its own independent search / device / date filters */}
      <div className="flex flex-wrap items-center gap-2">
        {view === 'schedules' && (
          <>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search task, device, vendor..." value={schedSearch} onChange={(e) => { setSchedSearch(e.target.value); setSchedPage(0); }} className="pl-9 pr-8 h-8 text-base md:text-sm" data-testid="service-schedules-search-input" />
              {schedSearch && (
                <button type="button" onClick={() => { setSchedSearch(''); setSchedPage(0); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" title="Clear search" data-testid="service-schedules-search-clear"><X className="w-4 h-4" /></button>
              )}
            </div>
            <DevicePicker devices={devices} value={schedDevice} onChange={(v) => { setSchedDevice(v); setSchedPage(0); }} allLabel="All Devices" placeholder="All Devices" className="w-52 h-8 text-sm" testId="service-schedules-device-filter" />
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setSchedPage(0); }}>
              <SelectTrigger className="w-36 h-8" data-testid="service-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="ok">OK</SelectItem>
                <SelectItem value="due_soon">Due Soon</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              {runCheckBtn}
              {scheduleActions}
            </div>
          </>
        )}
        {view === 'history' && (
          <>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search task, device, vendor..." value={histSearch} onChange={(e) => { setHistSearch(e.target.value); setHistPage(0); }} className="pl-9 pr-8 h-8 text-base md:text-sm" data-testid="service-history-search-input" />
              {histSearch && (
                <button type="button" onClick={() => { setHistSearch(''); setHistPage(0); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" title="Clear search" data-testid="service-history-search-clear"><X className="w-4 h-4" /></button>
              )}
            </div>
            <DevicePicker devices={devices} value={histDevice} onChange={(v) => { setHistDevice(v); setHistPage(0); }} allLabel="All Devices" placeholder="All Devices" className="w-52 h-8 text-sm" testId="service-history-device-filter" />
            <DateRangePresetFilter value={histRange} onChange={(r) => { setHistRange(r); setHistPage(0); }} testIdPrefix="service-history-date" resetSignal={histDateResetKey} />
            <Select value={`${histSortKey}:${histSortDir}`} onValueChange={(v) => { const [k, d] = v.split(':'); setHistSortKey(k); setHistSortDir(d); setHistPage(0); }}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="service-history-sort-key"><SelectValue placeholder="Sort by" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="completed_date:desc">Date New to Old</SelectItem>
                <SelectItem value="completed_date:asc">Date Old to New</SelectItem>
                <SelectItem value="vehicle:asc">Vehicle (A–Z)</SelectItem>
                <SelectItem value="cost:desc">Cost (High–Low)</SelectItem>
                <SelectItem value="task:asc">Task (A–Z)</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" className="h-8" onClick={() => { setHistRange({ from: undefined, to: undefined }); setHistDateResetKey((k) => k + 1); setHistPage(0); fetchHistory(0); }} data-testid="service-history-refresh-btn">
                <RefreshCw className="w-4 h-4 mr-1" /> Refresh
              </Button>
              {runCheckBtn}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8" disabled={exporting || selectedHistIds.length === 0} data-testid="service-history-export-btn">
                    {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} {selectedHistIds.length ? `Export (${selectedHistIds.length})` : 'Export'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => exportHistory('csv')} data-testid="service-history-export-csv">CSV</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportHistory('html')} data-testid="service-history-export-html">HTML</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportHistory('pdf')} data-testid="service-history-export-pdf">PDF</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </>
        )}
        {view === 'cost' && (
          <>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search task, vehicle, vendor..." value={costSearch} onChange={(e) => { setCostSearch(e.target.value); setCostPage(0); }} className="pl-9 pr-8 h-8 text-base md:text-sm" data-testid="service-cost-search-input" />
              {costSearch && (
                <button type="button" onClick={() => { setCostSearch(''); setCostPage(0); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" title="Clear search" data-testid="service-cost-search-clear"><X className="w-4 h-4" /></button>
              )}
            </div>
            <DevicePicker devices={devices} value={costDevice} onChange={(v) => { setCostDevice(v); setCostPage(0); }} allLabel="All Devices" placeholder="All Devices" className="w-52 h-8 text-sm" testId="service-cost-device-filter" />
            <DateRangePresetFilter value={costRange} onChange={(r) => { setCostRange(r); setCostPage(0); }} testIdPrefix="service-cost-date" resetSignal={costDateResetKey} />
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" className="h-8" onClick={() => { setCostRange({ from: undefined, to: undefined }); setCostDateResetKey((k) => k + 1); setCostPage(0); fetchCostReport(); }} data-testid="service-cost-refresh-btn">
                <RefreshCw className="w-4 h-4 mr-1" /> Refresh
              </Button>
              {runCheckBtn}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8" disabled={exporting || selectedCostIds.length === 0} data-testid="service-cost-export-btn">
                    {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} {selectedCostIds.length ? `Export (${selectedCostIds.length})` : 'Export'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => exportCostReport('csv')} data-testid="service-cost-export-csv">CSV</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportCostReport('html')} data-testid="service-cost-export-html">HTML</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportCostReport('pdf')} data-testid="service-cost-export-pdf">PDF</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </>
        )}
      </div>

      {/* Schedules list */}
      {view === 'schedules' && (
        <>
        {lastImportRun && (
          <div className="flex items-center gap-3 px-3 py-2 rounded-md bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 text-sm" data-testid="service-import-undo-banner">
            <Sparkles className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <span className="text-blue-800 dark:text-blue-200 flex-1">Imported {lastImportRun.created_count} created{lastImportRun.updated_count ? ` · ${lastImportRun.updated_count} updated` : ''}. You can undo this for 30 minutes.</span>
            <Button variant="outline" size="sm" className="h-7 text-xs" disabled={undoingImport} onClick={undoImport} data-testid="service-import-undo-btn">
              {undoingImport ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Undo2 className="w-3.5 h-3.5 mr-1" />} Undo import
            </Button>
            <button type="button" onClick={() => setLastImportRun(null)} className="text-blue-500 hover:text-blue-700" data-testid="service-import-undo-dismiss"><X className="w-4 h-4" /></button>
          </div>
        )}
        <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="service-schedules-selection-bar">
          <span data-testid="service-schedules-selection-count">{selectedSchedIds.length} selected</span>
          {selectedSchedIds.length > 0 && (<>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" disabled={schedSelectAllLoading} data-testid="service-schedules-select-all"
              onClick={async () => {
                if (selectedSchedIds.length >= schedTotal) { setSelectedSchedIds([]); return; }
                try {
                  setSchedSelectAllLoading(true);
                  const params = new URLSearchParams({ ids_only: 'true' });
                  if (schedDevice !== 'all') params.set('device_id', schedDevice);
                  if (statusFilter !== 'all') params.set('status', statusFilter);
                  if (schedSearchDebounced) params.set('search', schedSearchDebounced);
                  const res = await api.get(`/service/schedules?${params}`);
                  setSelectedSchedIds(res.data?.ids || []);
                } catch { toast.error('Failed to select all rows'); }
                finally { setSchedSelectAllLoading(false); }
              }}>
              {schedSelectAllLoading ? 'Selecting…' : (selectedSchedIds.length >= schedTotal ? 'Deselect All' : 'Select All')}
            </Button>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedSchedIds([])} data-testid="service-schedules-selection-clear">Clear</Button>
          </>)}
          {lastRefreshed && (
            <span className="ml-auto text-[11px] text-muted-foreground hidden sm:inline" title="Schedules refresh automatically every 60 seconds and when you return to this window" data-testid="service-last-refreshed">
              Updated {Math.max(0, Math.round((Date.now() - lastRefreshed) / 1000))}s ago
            </span>
          )}
        </div>
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : schedules.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="service-schedules-empty">
                <Wrench className="w-12 h-12 mx-auto mb-4 opacity-50" />
                {(schedSearchDebounced || schedDevice !== 'all' || statusFilter !== 'all') ? (
                  <p>No schedules match your filters</p>
                ) : (<>
                <p>No service schedules yet</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={openCreate} data-testid="service-empty-add">
                  <Plus className="w-4 h-4 mr-1" /> Add your first schedule
                </Button>
                </>)}
              </div>
            ) : (
              <>
              <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
                <Checkbox
                  checked={schedules.length > 0 && schedules.every(x => selectedSchedIds.includes(x.id))}
                  onCheckedChange={(checked) => {
                    const pageIds = schedules.map(x => x.id);
                    setSelectedSchedIds(prev => checked ? Array.from(new Set([...prev, ...pageIds])) : prev.filter(id => !pageIds.includes(id)));
                  }}
                  data-testid="service-schedules-select-page"
                />
                <span className="text-xs text-muted-foreground">Select page</span>
              </div>
              <div className="divide-y" data-testid="service-schedules-list">
                {schedules.map((s, idx) => {
                  const BasisIcon = BASIS_META[s.basis]?.icon || Gauge;
                  const isOpen = expanded.has(s.id);
                  const newGroup = idx === 0 || schedules[idx - 1].device_id !== s.device_id;
                  const groupCount = schedules.filter((x) => x.device_id === s.device_id).length;
                  const worst = schedules.filter((x) => x.device_id === s.device_id).some((x) => x.status === 'overdue') ? 'overdue'
                    : schedules.filter((x) => x.device_id === s.device_id).some((x) => x.status === 'due_soon') ? 'due_soon' : null;
                  return (
                    <React.Fragment key={s.id}>
                    {newGroup && (
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50/80 dark:bg-emerald-900/35 border-y border-emerald-100 dark:border-emerald-800/60 text-xs" data-testid={`service-group-${s.device_id}`}>
                        <Truck className="w-3.5 h-3.5 text-emerald-700 dark:text-emerald-300" />
                        <span className="font-bold text-emerald-700 dark:text-emerald-300">{deviceLabel(s)}</span>
                        <span className="text-muted-foreground">{groupCount} schedule{groupCount === 1 ? '' : 's'}</span>
                        {worst && <Badge variant="outline" className={`ml-auto text-[10px] px-1.5 py-0 ${STATUS_STYLES[worst] || ''}`}>{worst.replace('_', ' ')}</Badge>}
                      </div>
                    )}
                    <div className="px-3 py-2.5" data-testid={`service-schedule-${s.id}`}>
                      <div className="flex items-start gap-2.5">
                        <Checkbox className="mt-1 flex-shrink-0" checked={selectedSchedIds.includes(s.id)} onCheckedChange={() => setSelectedSchedIds(prev => prev.includes(s.id) ? prev.filter(id => id !== s.id) : [...prev, s.id])} data-testid={`service-schedule-check-${s.id}`} />
                        <button className="mt-0.5 text-muted-foreground" onClick={() => setExpanded(prev => { const n = new Set(prev); n.has(s.id) ? n.delete(s.id) : n.add(s.id); return n; })} data-testid={`service-expand-${s.id}`}>
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                        <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5"><BasisIcon className="w-3.5 h-3.5 text-slate-600" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-sm font-semibold break-words">{s.task_name}</span>
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${STATUS_STYLES[s.status] || ''}`}>{(s.status || 'ok').replace('_', ' ')}</Badge>
                            {s.recurring && <Badge variant="outline" className="text-[10px] px-1.5 py-0">recurring</Badge>}
                            {!s.is_active && <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-slate-100">inactive</Badge>}
                            {s.basis_source_off && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200" data-testid={`service-source-off-badge-${s.id}`}>
                                <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />{s.basis === 'odometer' ? 'odometer' : 'engine hours'} source off
                              </Badge>
                            )}
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-0.5 break-words">
                            <span data-testid={`service-schedule-device-${s.id}`}>{deviceLabel(s)}</span> • Every {intervalText(s)} • Due at {dueLabel(s)} • <span className={(s.remaining != null && s.remaining < 0) || (s.remaining_days != null && s.remaining_days < 0) ? 'text-red-600 font-medium' : ''}>{remainingLabel(s)}</span>
                            {(() => { const p = predictedInfo(s); return p ? (<> • <span className={`cursor-help underline decoration-dotted ${p.text.startsWith('est.') ? 'text-sky-700 dark:text-sky-300 font-medium' : ''}`} title={p.title} data-testid={`service-predicted-${s.id}`}>{p.text}</span></>) : null; })()}
                          </p>
                        </div>
                        <div className="flex gap-1.5 flex-wrap flex-shrink-0">
                          {(s.attachments || []).length > 0 && (
                            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] gap-1" onClick={() => setAttachmentsFor(s)} title="View schedule documents" data-testid={`service-schedule-attachments-${s.id}`}>
                              <Paperclip className="w-3 h-3" /> {s.attachments.length}
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openComplete(s)} data-testid={`service-complete-${s.id}`}><CheckCircle className="w-3.5 h-3.5 mr-1" /> Log Service</Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDuplicating(s)} title="Duplicate to other assets" data-testid={`service-duplicate-${s.id}`}><Copy className="w-3.5 h-3.5" /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(s)} data-testid={`service-edit-${s.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => deleteSchedule(s)} data-testid={`service-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                      {isOpen && (
                        <div className="mt-2 ml-9 pl-2 border-l space-y-1.5" data-testid={`service-stages-${s.id}`}>
                          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Reminder stages ({(s.reminder_stages || []).length})</p>
                          {(s.reminder_stages || []).length === 0 && <p className="text-[11px] text-muted-foreground">No reminder stages.</p>}
                          {(s.reminder_stages || []).map((st, i) => {
                            const suffix = st.trigger_point === 'after' ? 'overdue' : 'before';
                            const dualParts = isDualBasis(s.basis)
                              ? [Number(st.trigger_value) > 0 && `${st.trigger_value} ${unitFor(s.basis)}`, Number(st.trigger_days) > 0 && `${st.trigger_days} days`].filter(Boolean)
                              : null;
                            const pointTxt = st.trigger_point === 'at'
                              ? 'At due'
                              : dualParts
                                ? (dualParts.length ? `${dualParts.join(' or ')} ${suffix}` : `At due (${suffix} thresholds not set)`)
                                : `${st.trigger_value} ${unitFor(s.basis)} ${suffix}`;
                            const channels = [];
                            if (st.notifications?.system_message !== false) channels.push('In-app');
                            if (st.notifications?.notify_bell) channels.push('Bell');
                            if (st.notifications?.email_enabled || st.notifications?.email_addresses) channels.push('Email');
                            if (st.notifications?.sms_enabled || st.notifications?.sms_numbers) channels.push('SMS');
                            if (st.actions?.webhook_enabled) channels.push('Webhook');
                            if (st.actions?.command_on_trigger_enabled) channels.push('Command');
                            return (
                              <div key={i} className="flex items-center gap-2 text-[11px] flex-wrap">
                                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${SEVERITY_STYLES[st.severity] || ''}`}>{st.severity}</Badge>
                                {st.raise_alert === false && <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-sky-50 text-sky-700 border-sky-200" data-testid={`service-stage-notify-only-chip-${s.id}-${i}`}>notify only</Badge>}
                                <span className="text-muted-foreground">{st.label || `Stage ${i + 1}`}: {pointTxt}</span>
                                {channels.length > 0 && <span className="text-muted-foreground">→ {channels.join(', ')}</span>}
                                {st.fired_at && <Badge variant="outline" className="text-[9px] px-1 py-0 bg-emerald-50 text-emerald-700 border-emerald-200">fired</Badge>}
                              </div>
                            );
                          })}
                          {s.notes && <p className="text-[11px] text-muted-foreground pt-1">Notes: {s.notes}</p>}
                          {s.last_checked_at && (
                            <p className="text-[10px] text-muted-foreground pt-1" data-testid={`service-last-checked-${s.id}`}>
                              Engine last checked {formatDT(s.last_checked_at, { timezone: tz, seconds: false })}{intervalLabel ? ` · auto-checks every ${intervalLabel}` : ''}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    </React.Fragment>
                  );
                })}
              </div>
              </>
            )}
            <PaginationFooter page={schedPage} setPage={setSchedPage} totalItems={schedTotal} itemsPerPage={schedPageSize} setItemsPerPage={setSchedPageSize} testIdPrefix="svc-schedules" zeroIndexed disabled={loading} alwaysShow />
          </CardContent>
        </Card>
        </>
      )}

      {/* History list */}
      {view === 'history' && (
        <>
        {/* Selection bar (Alerts-style) — sits above the card, count always visible */}
        <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="service-history-selection-bar">
          <span data-testid="service-history-selection-count">{selectedHistIds.length} selected</span>
          {selectedHistIds.length > 0 && (<>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" disabled={histSelectAllLoading} data-testid="service-history-select-all"
              onClick={async () => {
                if (selectedHistIds.length >= historyTotal) { setSelectedHistIds([]); return; }
                try {
                  setHistSelectAllLoading(true);
                  const params = new URLSearchParams({ ids_only: 'true' });
                  if (histDevice !== 'all') params.set('device_id', histDevice);
                  if (histSearchDebounced) params.set('search', histSearchDebounced);
                  if (histRange.from) params.set('date_from', histRange.from.toISOString());
                  if (histRange.to) params.set('date_to', histRange.to.toISOString());
                  const res = await api.get(`/service/history?${params}`);
                  setSelectedHistIds(res.data?.ids || []);
                } catch { toast.error('Failed to select all rows'); }
                finally { setHistSelectAllLoading(false); }
              }}>
              {histSelectAllLoading ? 'Selecting…' : (selectedHistIds.length >= historyTotal ? 'Deselect All' : 'Select All')}
            </Button>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedHistIds([])} data-testid="service-history-selection-clear">Clear</Button>
          </>)}
        </div>
        <Card>
          <CardContent className="p-0">
            {histLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : history.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="service-history-empty">
                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{(histSearchDebounced || histDevice !== 'all' || histRange.from || histRange.to) ? 'No services match your filters' : 'No completed services yet'}</p>
              </div>
            ) : (
              <>
              {/* Page select-all header */}
              <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
                <Checkbox
                  checked={history.length > 0 && history.every(h => selectedHistIds.includes(h.id))}
                  onCheckedChange={(checked) => {
                    const pageIds = history.map(h => h.id);
                    setSelectedHistIds(prev => checked ? Array.from(new Set([...prev, ...pageIds])) : prev.filter(id => !pageIds.includes(id)));
                  }}
                  data-testid="service-history-select-page"
                />
                <span className="text-xs text-muted-foreground">Select page</span>
              </div>
              <div className="divide-y" data-testid="service-history-list">
                {history.map((h) => (
                  <div key={h.id} className="px-3 py-2.5 flex items-start gap-2.5" data-testid={`service-history-${h.id}`}>
                    <Checkbox className="mt-1 flex-shrink-0" checked={selectedHistIds.includes(h.id)} onCheckedChange={(checked) => setSelectedHistIds(prev => checked ? [...prev, h.id] : prev.filter(id => id !== h.id))} data-testid={`service-history-select-${h.id}`} />
                    <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5"><CheckCircle className="w-3.5 h-3.5 text-green-600" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-semibold break-words">{h.task_name}</span>
                        {h.cost != null && <Badge variant="outline" className="text-[10px] px-1.5 py-0">${Number(h.cost).toFixed(2)}</Badge>}
                      </div>
                      <p className="text-[11px] mt-0.5 break-words text-muted-foreground">
                        <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300" data-testid={`service-history-device-${h.id}`}>{h.asset_name || h.device_name || 'Device'}</span>
                        {' • '}<span data-testid={`service-history-date-${h.id}`} title={`Serviced ${formatDT(h.completed_date, { timezone: tz, seconds: false })}`}>{formatDateOnly(h.completed_date, { timezone: tz })}</span>
                        {h.odometer_at_service != null ? ` • ${h.odometer_at_service} km` : ''}
                        {h.engine_hours_at_service != null ? ` • ${h.engine_hours_at_service} hrs` : ''}
                        {h.vendor ? ` • ${h.vendor}` : ''}
                        {h.performed_by ? <> • by <span data-testid={`service-history-performed-by-${h.id}`}>{h.performed_by}</span></> : ''}
                      </p>
                      {h.notes && <p className="text-[11px] text-muted-foreground mt-0.5 break-words">{h.notes}</p>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {(h.attachments || []).length > 0 && (
                        <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] gap-1" onClick={() => setAttachmentsFor(h)} title="View attachments" data-testid={`service-history-attachments-${h.id}`}>
                          <Paperclip className="w-3 h-3" /> {h.attachments.length}
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditHistory(h)} title="Edit service log" data-testid={`service-history-edit-${h.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => deleteHistoryRow(h)} title="Delete service log" data-testid={`service-history-delete-${h.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
              </>
            )}
            <PaginationFooter page={histPage} setPage={setHistPage} totalItems={historyTotal} itemsPerPage={histPageSize} setItemsPerPage={setHistPageSize} testIdPrefix="svc-history" zeroIndexed disabled={histLoading} alwaysShow />
          </CardContent>
        </Card>
        </>
      )}

      {/* Cost Report — per-vehicle spend, ranked, expandable per-task breakdown */}
      {view === 'cost' && (
        <>
        <CostTrends api={api} onFilter={({ deviceId, task, monthKey }) => {
          if (deviceId === 'all') {
            setCostDevice('all'); setCostSearch(''); setCostRange({ from: undefined, to: undefined }); setCostDateResetKey((k) => k + 1); setCostPage(0); return;
          }
          if (task) { setCostSearch(task); setCostDevice('all'); }
          else if (deviceId) { setCostDevice(deviceId); setCostSearch(''); }
          if (monthKey) {
            const [yy, mm] = monthKey.split('-').map(Number);
            setCostRange({ from: new Date(yy, mm - 1, 1), to: new Date(yy, mm, 0, 23, 59, 59, 999) });
          } else {
            setCostRange({ from: undefined, to: undefined }); setCostDateResetKey((k) => k + 1);
          }
          setCostPage(0);
        }} />
        {/* Selection bar (Alerts-style) — sits above the card, count always visible */}
        <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="service-cost-selection-bar">
          <span data-testid="service-cost-selection-count">{selectedCostIds.length} selected</span>
          {selectedCostIds.length > 0 && (<>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" disabled={costSelectAllLoading} data-testid="service-cost-select-all"
              onClick={async () => {
                if (selectedCostIds.length >= costTotal) { setSelectedCostIds([]); return; }
                try {
                  setCostSelectAllLoading(true);
                  const params = new URLSearchParams({ ids_only: 'true' });
                  if (costDevice !== 'all') params.set('device_id', costDevice);
                  if (costRange.from) params.set('date_from', costRange.from.toISOString());
                  if (costRange.to) params.set('date_to', costRange.to.toISOString());
                  if (costSearchDebounced) params.set('search', costSearchDebounced);
                  const res = await api.get(`/service/cost-report?${params}`);
                  setSelectedCostIds(res.data?.ids || []);
                } catch { toast.error('Failed to select all rows'); }
                finally { setCostSelectAllLoading(false); }
              }}>
              {costSelectAllLoading ? 'Selecting…' : (selectedCostIds.length >= costTotal ? 'Deselect All' : 'Select All')}
            </Button>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedCostIds([])} data-testid="service-cost-selection-clear">Clear</Button>
          </>)}
        </div>
        <Card>
          <CardContent className="p-0">
            {costLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : !(costReport?.items || []).length ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="service-cost-empty">
                <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{(costDevice !== 'all' || costRange.from || costRange.to) ? 'No maintenance spend for this selection' : 'No maintenance spend recorded yet'}</p>
              </div>
            ) : (
              <>
              {/* Page select-all header */}
              <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
                <Checkbox
                  checked={costReport.items.length > 0 && costReport.items.every(v => selectedCostIds.includes(v.device_id))}
                  onCheckedChange={(checked) => {
                    const pageIds = costReport.items.map(v => v.device_id);
                    setSelectedCostIds(prev => checked ? Array.from(new Set([...prev, ...pageIds])) : prev.filter(id => !pageIds.includes(id)));
                  }}
                  data-testid="service-cost-select-page"
                />
                <span className="text-xs text-muted-foreground">Select page</span>
              </div>
              <div className="divide-y" data-testid="service-cost-list">
                {costReport.items.map((v) => {
                  const maxSpend = costReport.summary?.max_spend || 0;
                  const pct = maxSpend > 0 ? Math.max(2, Math.round((v.total_spend / maxSpend) * 100)) : 0;
                  const isOpen = expandedCost.has(v.device_id);
                  const checked = selectedCostIds.includes(v.device_id);
                  return (
                    <div key={v.device_id} data-testid={`service-cost-row-${v.device_id}`}>
                      <div className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-muted/40 transition-colors">
                        <Checkbox className="flex-shrink-0" checked={checked} onCheckedChange={(c) => setSelectedCostIds(prev => c ? [...prev, v.device_id] : prev.filter(id => id !== v.device_id))} data-testid={`service-cost-select-${v.device_id}`} />
                        <button
                          type="button"
                          className="flex-1 min-w-0 text-left flex items-center gap-2.5"
                          onClick={() => setExpandedCost((prev) => { const n = new Set(prev); n.has(v.device_id) ? n.delete(v.device_id) : n.add(v.device_id); return n; })}
                          data-testid={`service-cost-toggle-${v.device_id}`}
                        >
                          <span className="flex-shrink-0 text-muted-foreground">{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300 truncate" data-testid={`service-cost-name-${v.device_id}`}>
                                {v.label}{v.unit_number ? <span className="text-[11px] font-normal text-muted-foreground ml-1">#{v.unit_number}</span> : ''}
                              </span>
                              <span className="text-sm font-semibold tabular-nums flex-shrink-0" data-testid={`service-cost-spend-${v.device_id}`}>{money(v.total_spend)}</span>
                            </div>
                            {/* Inline data bar — length proportional to the top spender */}
                            <div className="mt-1.5 h-1.5 w-full rounded-full bg-muted overflow-hidden">
                              <div className="h-full rounded-full bg-orange-500 dark:bg-orange-500/80" style={{ width: `${pct}%` }} />
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                              <span>{v.service_count} service{v.service_count === 1 ? '' : 's'}{v.avg_cost != null ? ` • avg ${money(v.avg_cost)}` : ''}</span>
                              {v.cost_per_1000km != null && (
                                <span className="inline-flex items-center rounded bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300 px-1.5 py-0.5" title={`Over ${Number(v.distance_km).toLocaleString()} km travelled between services in this range`} data-testid={`service-cost-per-km-${v.device_id}`}>
                                  {money(v.cost_per_1000km)} / 1,000 km
                                </span>
                              )}
                              {v.cost_per_100hrs != null && (
                                <span className="inline-flex items-center rounded bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300 px-1.5 py-0.5" title={`Over ${Number(v.engine_hours).toLocaleString()} engine hours between services in this range`} data-testid={`service-cost-per-hr-${v.device_id}`}>
                                  {money(v.cost_per_100hrs)} / 100 hrs
                                </span>
                              )}
                            </p>
                          </div>
                        </button>
                      </div>
                      {isOpen && (
                        <div className="px-3 pb-3 pl-11 space-y-1" data-testid={`service-cost-services-${v.device_id}`}>
                          {(v.services || []).map((s, i) => (
                            <div key={s.id || i} className="flex items-center justify-between gap-2 text-[11px] py-1 border-b border-dashed border-border/60 last:border-0" data-testid={`service-cost-service-${v.device_id}-${i}`}>
                              <span className="flex items-center gap-2 min-w-0">
                                <span className="text-muted-foreground tabular-nums flex-shrink-0" title={formatDT(s.completed_date, { timezone: tz, seconds: false })}>{formatDateOnly(s.completed_date, { timezone: tz })}</span>
                                <span className="truncate text-foreground">{s.task || '—'}</span>
                              </span>
                              <span className="font-medium text-foreground tabular-nums flex-shrink-0">{money(s.cost)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              </>
            )}
            <PaginationFooter page={costPage} setPage={setCostPage} totalItems={costTotal} itemsPerPage={costPageSize} setItemsPerPage={setCostPageSize} testIdPrefix="svc-cost" zeroIndexed disabled={costLoading} alwaysShow />
          </CardContent>
        </Card>
        </>
      )}

      {/* ── Schedule editor dialog ── */}
      <Dialog open={editorOpen} onOpenChange={(o) => { if (!o) closeEditor(); else setEditorOpen(true); }}>
        <DialogContent className="touch-dialog w-[95vw] max-w-2xl h-[85vh] sm:h-[85vh] flex flex-col p-0" data-testid="service-editor-dialog">
          <DialogHeader className="px-6 pt-6 pb-2">
            <div className="flex items-start justify-between gap-3 pr-8">
              <DialogTitle>{editingId ? 'Edit Service Schedule' : 'New Service Schedule'}</DialogTitle>
              <ScheduleHelpPanel />
            </div>
            <DialogDescription>Set the interval and add reminder stages that raise alerts and send notifications.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">Device / Asset<MaintHelpTip topic="device" testId="service-help-device" /></Label>
                <DevicePicker
                  devices={devices}
                  value={form.device_id}
                  onChange={(v) => {
                    const d = devicesById[v];
                    setForm(prev => {
                      let basis = prev.basis;
                      if (d) {
                        if (usesOdometer(basis) && !['gps', 'sensor'].includes(d.odometer_source)) basis = 'calendar';
                        if (usesEngineHours(basis) && !['ignition', 'sensor'].includes(d.engine_hours_source)) basis = 'calendar';
                      }
                      return { ...prev, device_id: v, basis };
                    });
                  }}
                  disabled={!!editingId}
                  placeholder="Select device"
                  className="w-full h-9"
                  testId="service-form-device"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">Task Preset<MaintHelpTip topic="preset" testId="service-help-preset" /></Label>
                <Select value={form.task_preset} onValueChange={(v) => { setField('task_preset', v); const p = presets.find(x => x.key === v); if (p && v !== 'custom') setField('task_name', p.label); }}>
                  <SelectTrigger className="h-9" data-testid="service-form-preset"><SelectValue /></SelectTrigger>
                  <SelectContent>{presets.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-xs">Task Name</Label>
                <Input value={form.task_name} onChange={(e) => setField('task_name', e.target.value)} placeholder="e.g. Engine Oil Change" className="h-9" data-testid="service-form-task-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">Schedule Basis<MaintHelpTip topic="basis" testId="service-help-basis" /></Label>
                <Select value={form.basis} onValueChange={(v) => setField('basis', v)} disabled={!!editingId}>
                  <SelectTrigger className="h-9" data-testid="service-form-basis"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="odometer" disabled={odoOff}>Odometer (km){odoOff ? ' — source off' : ''}</SelectItem>
                    <SelectItem value="engine_hours" disabled={ehOff}>Engine Hours{ehOff ? ' — source off' : ''}</SelectItem>
                    <SelectItem value="calendar">Calendar (days)</SelectItem>
                    <SelectItem value="odometer_calendar" disabled={odoOff}>Odometer + Calendar — whichever first{odoOff ? ' (source off)' : ''}</SelectItem>
                    <SelectItem value="engine_hours_calendar" disabled={ehOff}>Engine Hours + Calendar — whichever first{ehOff ? ' (source off)' : ''}</SelectItem>
                  </SelectContent>
                </Select>
                {selDev && (odoOff || ehOff) && (
                  <p className="text-[10px] text-amber-600" data-testid="service-source-off-hint">
                    {[odoOff && 'Odometer', ehOff && 'Engine hours'].filter(Boolean).join(' & ')} source is off for this vehicle — enable it in Fleet › Edit Asset
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">Interval ({unitFor(form.basis)})<MaintHelpTip topic="interval" testId="service-help-interval" /></Label>
                <Input type="number" min="1" value={form.interval_value} onChange={(e) => setField('interval_value', e.target.value)} placeholder={form.basis === 'calendar' ? '90' : usesOdometer(form.basis) ? '10000' : '250'} className="h-9" data-testid="service-form-interval" />
              </div>
              {isDualBasis(form.basis) && (
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1">Interval (days)<MaintHelpTip topic="interval_days" testId="service-help-interval-days" /></Label>
                  <Input type="number" min="1" value={form.interval_days} onChange={(e) => setField('interval_days', e.target.value)} placeholder="180" className="h-9" data-testid="service-form-interval-days" />
                  <p className="text-[10px] text-muted-foreground">Due at whichever comes first — {unitFor(form.basis)} or days</p>
                </div>
              )}
              {form.basis !== 'calendar' && (
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-xs flex items-center gap-1">Last Serviced {unitFor(form.basis)} <span className="text-red-500">*</span><MaintHelpTip topic="baseline_value" testId="service-help-baseline-value" /></Label>
                  <Input type="number" value={form.baseline_value} onChange={(e) => setField('baseline_value', e.target.value)} placeholder={`Enter the reading at the last service (${unitFor(form.basis)})`} className="h-9" data-testid="service-form-baseline-value" />
                  {currentReading != null && (
                    <p className="text-[10px] text-muted-foreground" data-testid="service-current-reading">
                      For reference — current {usesOdometer(form.basis) ? 'odometer' : 'engine hours'}: <span className="font-medium">{currentReading} {unitFor(form.basis)}</span> ({sourceLabel})
                    </p>
                  )}
                  {editingId && editingLatestLog?.value != null && form.baseline_value !== '' && Number(form.baseline_value) !== Number(editingLatestLog.value) && (
                    <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-2 py-1.5 text-[11px] text-amber-800 dark:text-amber-300" data-testid="service-form-latest-log-mismatch">
                      <span>Latest service log ({formatDateOnly(editingLatestLog.completed_date, { timezone: tz })}) recorded <b>{editingLatestLog.value} {unitFor(form.basis)}</b>, but Last Serviced is {form.baseline_value} {unitFor(form.basis)}.</span>
                      <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-[11px] border-amber-400" onClick={() => setField('baseline_value', editingLatestLog.value)} data-testid="service-form-use-latest-log">Use latest log</Button>
                    </div>
                  )}
                </div>
              )}
              {(form.basis === 'calendar' || isDualBasis(form.basis)) && (
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1">Last Serviced Date <span className="text-red-500">*</span><MaintHelpTip topic="baseline_date" testId="service-help-baseline-date" /></Label>
                  <Input type="date" value={form.baseline_date} onChange={(e) => setField('baseline_date', e.target.value)} className="h-9" data-testid="service-form-baseline-date" />
                </div>
              )}
              <div className="flex items-center gap-4 sm:col-span-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch checked={form.recurring} onCheckedChange={(v) => setField('recurring', v)} data-testid="service-form-recurring" />
                  Recurring (roll forward after each service)
                  <MaintHelpTip topic="recurring" testId="service-help-recurring" />
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch checked={form.is_active} onCheckedChange={(v) => setField('is_active', v)} data-testid="service-form-active" />
                  Active
                </label>
              </div>
              {form.basis !== 'calendar' && form.recurring && (
                <div className="space-y-1 sm:col-span-2 rounded-md border border-border bg-muted/30 px-3 py-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Switch checked={form.fixed_schedule} onCheckedChange={(v) => setField('fixed_schedule', v)} data-testid="service-form-fixed-schedule" />
                    Keep a fixed service schedule
                  </label>
                  <p className="text-[10px] text-muted-foreground">
                    {form.fixed_schedule
                      ? `Next due always lands on a fixed grid from your Last Serviced value (e.g. 10,000 / 20,000 / 30,000 ${unitFor(form.basis)}). Servicing a little early or late won't shift the schedule.`
                      : `Rolling — next due = the actual reading when serviced + the interval.`}
                  </p>
                </div>
              )}
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-xs">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setField('notes', e.target.value)} rows={2} data-testid="service-form-notes" />
              </div>
              <div className="space-y-1.5 sm:col-span-2" data-testid="service-form-documents">
                <Label className="text-xs">Documents (specs / checklists / manuals)</Label>
                <div className="flex items-center gap-2">
                  <input type="file" id="svc-sched-file" className="hidden" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.csv,.txt" onChange={(e) => { uploadScheduleDoc(e.target.files); e.target.value = ''; }} data-testid="service-form-file-input" />
                  <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('svc-sched-file').click()} disabled={uploading} data-testid="service-form-upload-btn">
                    {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Add files
                  </Button>
                  <span className="text-[11px] text-muted-foreground" data-testid="service-form-documents-count">{(form.attachments || []).length ? `${form.attachments.length} attached` : 'No documents yet'}</span>
                </div>
                {(form.attachments || []).length > 0 && (
                  <ul className="divide-y rounded-md border text-xs" data-testid="service-form-documents-list">
                    {form.attachments.map((a, i) => (
                      <li key={a.id || a.url} className="flex items-center gap-2 px-2 py-1.5">
                        <Paperclip className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                        <button type="button" className="flex-1 min-w-0 truncate text-left text-blue-600 dark:text-blue-400 hover:underline" onClick={() => openAttachment(a)} title="Open in new tab" data-testid={`service-form-document-${i}`}>{a.name || 'Document'}</button>
                        <span className="text-[10px] text-muted-foreground flex-shrink-0">{fmtSize(a.size)}</span>
                        <Button type="button" size="icon" variant="outline" className="h-7 w-7 flex-shrink-0" onClick={() => downloadAttachment(a)} title="Download" data-testid={`service-form-document-download-${i}`}><Download className="w-3.5 h-3.5" /></Button>
                        <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-red-500 flex-shrink-0" onClick={() => setPendingRemoveAtt({ att: a, kind: 'schedule' })} title="Remove (deletes the file)" data-testid={`service-form-document-remove-${i}`}><X className="w-3.5 h-3.5" /></Button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[10px] text-muted-foreground">Stored permanently with this schedule. PDF, images, Word, CSV or TXT — up to 15 MB each.</p>
              </div>
            </div>

            {/* Reminder stages */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-1.5"><Bell className="w-4 h-4" /> Reminder Stages</Label>
              {form.reminder_stages.map((st, idx) => (
                <div key={st.id} className={`rounded-lg border p-3 space-y-3 ${STAGE_CARD_STYLES[idx % STAGE_CARD_STYLES.length]}`} data-testid={`service-stage-${idx}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Input value={st.label} onChange={(e) => updateStage(idx, { label: e.target.value })} placeholder={`Stage ${idx + 1} label (e.g. Parts dept)`} className="h-8 text-xs flex-1 min-w-[140px]" data-testid={`service-stage-label-${idx}`} />
                    {form.reminder_stages.length > 1 && (
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => setForm(prev => ({ ...prev, reminder_stages: prev.reminder_stages.filter((_, i) => i !== idx) }))} data-testid={`service-stage-remove-${idx}`}><X className="w-3.5 h-3.5" /></Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div className="space-y-1">
                      <Label className="text-[10px] flex items-center gap-1 min-h-[14px]">When<MaintHelpTip topic="stage_when" testId={`service-help-stage-when-${idx}`} /></Label>
                      <Select value={st.trigger_point} onValueChange={(v) => updateStage(idx, { trigger_point: v })}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`service-stage-point-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="before">Before due</SelectItem>
                          <SelectItem value="at">At due</SelectItem>
                          <SelectItem value="after">After due (overdue)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {st.trigger_point !== 'at' && (
                      <div className="space-y-1">
                        <Label className="text-[10px] flex items-center min-h-[14px]">{unitFor(form.basis)} {st.trigger_point === 'after' ? 'overdue' : 'before'}</Label>
                        <Input type="number" min="0" value={st.trigger_value} onChange={(e) => updateStage(idx, { trigger_value: e.target.value })} className="h-8 text-xs" data-testid={`service-stage-value-${idx}`} />
                      </div>
                    )}
                    {st.trigger_point !== 'at' && isDualBasis(form.basis) && (
                      <div className="space-y-1">
                        <Label className="text-[10px] flex items-center min-h-[14px]">days {st.trigger_point === 'after' ? 'overdue' : 'before'} (0 = not set)</Label>
                        <Input type="number" min="0" value={st.trigger_days} onChange={(e) => updateStage(idx, { trigger_days: e.target.value })} className="h-8 text-xs" data-testid={`service-stage-days-${idx}`} />
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label className="text-[10px] flex items-center gap-1 min-h-[14px]">Severity<MaintHelpTip topic="stage_severity" testId={`service-help-stage-severity-${idx}`} /></Label>
                      <Select value={st.severity} onValueChange={(v) => updateStage(idx, { severity: v })}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`service-stage-severity-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer flex-wrap">
                    <Switch checked={st.raise_alert !== false} onCheckedChange={(v) => updateStage(idx, { raise_alert: v })} data-testid={`service-stage-raise-alert-${idx}`} />
                    <span className="font-medium">Raise Alert in inbox</span>
                    {st.raise_alert === false && (
                      <span className="text-muted-foreground" data-testid={`service-stage-notify-only-hint-${idx}`}>
                        Notify only — sends this stage&apos;s notifications &amp; actions without an Alerts-inbox entry
                      </span>
                    )}
                  </label>
                  <Tabs defaultValue="notifications">
                    <TabsList className="grid grid-cols-2 w-full h-8">
                      <TabsTrigger value="notifications" className="text-xs" data-testid={`service-stage-tab-notif-${idx}`}>Notifications</TabsTrigger>
                      <TabsTrigger value="actions" className="text-xs" data-testid={`service-stage-tab-actions-${idx}`}>Actions</TabsTrigger>
                    </TabsList>
                    <TabsContent value="notifications" className="pt-2">
                      <NotificationsConfig
                        notifications={st.notifications}
                        onChange={(k, v) => updateStageNotif(idx, k, v)}
                        hasPushNotif={hasPushNotif} hasEmailNotif={hasEmailNotif} hasSmsNotif={hasSmsNotif}
                        pushDisabledReason={pushDisabledReason} emailDisabledReason={emailDisabledReason} smsDisabledReason={smsDisabledReason}
                        notifTemplates={notifTemplates} onManageTemplate={openTemplateManager}
                        supportsTransitionCardColorTiming={false}
                      />
                    </TabsContent>
                    <TabsContent value="actions" className="pt-2 space-y-3">
                      <WebhookActionConfig actions={st.actions} onChange={(k, v) => updateStageAction(idx, k, v)} api={api} testPath="/event-rules/test-webhook" />
                      <div className="p-3 rounded-lg border space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="font-medium text-sm flex items-center gap-1.5"><Wrench className="w-4 h-4" /> Send Device Command</p>
                            <p className="text-xs text-muted-foreground">Send a command to the vehicle when this stage triggers</p>
                          </div>
                          <Switch checked={st.actions.command_on_trigger_enabled || false} onCheckedChange={(v) => updateStageAction(idx, 'command_on_trigger_enabled', v)} data-testid={`service-stage-cmd-toggle-${idx}`} />
                        </div>
                        {st.actions.command_on_trigger_enabled && (
                          <CommandActionConfig
                            config={st.actions.command_on_trigger || {}}
                            onChange={(k, v) => updateStageCommand(idx, k, v)}
                            devices={devices}
                            selectedDeviceIds={editStageDeviceIds}
                            hasSmsNotif={hasSmsNotif}
                            testIdPrefix={`service-stage-cmd-${idx}`}
                          />
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              ))}
              <Button type="button" size="sm" className="w-full h-9 text-xs font-semibold bg-orange-500 hover:bg-orange-600 text-white border border-dashed border-orange-300" onClick={() => setForm(prev => ({ ...prev, reminder_stages: [...prev.reminder_stages, makeStage()] }))} data-testid="service-add-stage-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Stage</Button>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t">
            <Button variant="outline" size="sm" onClick={closeEditor} data-testid="service-editor-cancel">Cancel</Button>
            <Button size="sm" onClick={saveSchedule} disabled={saving} data-testid="service-editor-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {editingId ? 'Save Schedule' : 'Create Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScheduleImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        api={api}
        devices={devices}
        existingSchedules={schedules}
        dateFormat={userDateFormat}
        onImported={(run) => { if (run) setLastImportRun(run); fetchSchedules(); fetchSummary(); fetchLatestImportRun(); }}
      />

      {/* ── Bulk Log Service ── */}      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) setBulkOpen(false); }}>
        <DialogContent className="w-[95vw] max-w-3xl h-[85vh] flex flex-col p-0" data-testid="service-bulk-dialog">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>Bulk Log Service {bulkStep === 2 ? `— ${bulkSelectedIds.length} selected` : ''}</DialogTitle>
            <DialogDescription>
              {bulkStep === 1 ? 'Pick the vehicles you serviced. You can mix different tasks.' : 'Shared details apply to every service. Each vehicle keeps its own reading and can override the cost.'}
            </DialogDescription>
          </DialogHeader>

          {bulkStep === 1 && (
            <div className="flex-1 overflow-hidden flex flex-col px-6">
              <div className="flex flex-wrap items-center gap-2 pb-2">
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search vehicle or task..." value={bulkSearch} onChange={(e) => setBulkSearch(e.target.value)} className="pl-9 pr-8 h-8 text-sm" data-testid="service-bulk-search" />
                  {bulkSearch && <button type="button" onClick={() => setBulkSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}
                </div>
                <Select value={bulkTaskFilter} onValueChange={setBulkTaskFilter}>
                  <SelectTrigger className="w-48 h-8 text-sm" data-testid="service-bulk-task-filter"><SelectValue placeholder="All tasks" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All tasks</SelectItem>
                    {Array.from(new Set(bulkSchedules.map((s) => s.task_name).filter(Boolean))).sort().map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(() => {
                const list = bulkSchedules.filter((s) => (bulkTaskFilter === 'all' || s.task_name === bulkTaskFilter) &&
                  (!bulkSearch || `${s.task_name} ${deviceLabel(s)}`.toLowerCase().includes(bulkSearch.toLowerCase())));
                const allSel = list.length > 0 && list.every((s) => bulkSelectedIds.includes(s.id));
                return (
                  <>
                    <div className="flex items-center gap-2 pb-1.5 text-xs text-muted-foreground">
                      <Checkbox checked={allSel} onCheckedChange={(c) => {
                        const ids = list.map((s) => s.id);
                        setBulkSelectedIds((prev) => c ? Array.from(new Set([...prev, ...ids])) : prev.filter((id) => !ids.includes(id)));
                      }} data-testid="service-bulk-select-all" />
                      <span>Select all ({list.length})</span>
                      <span className="ml-auto">{bulkSelectedIds.length} selected</span>
                    </div>
                    <div className="flex-1 overflow-y-auto border rounded-md divide-y" data-testid="service-bulk-list">
                      {bulkLoading ? (
                        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
                      ) : list.length === 0 ? (
                        <div className="text-center py-12 text-sm text-muted-foreground">No open schedules match.</div>
                      ) : list.map((s) => (
                        <label key={s.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-muted/40" data-testid={`service-bulk-item-${s.id}`}>
                          <Checkbox checked={bulkSelectedIds.includes(s.id)} onCheckedChange={() => bulkToggle(s.id)} data-testid={`service-bulk-check-${s.id}`} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-sm font-medium">{deviceLabel(s)}</span>
                              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${STATUS_STYLES[s.status] || ''}`}>{(s.status || 'ok').replace('_', ' ')}</Badge>
                              {s.recently_logged && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900" title={`A service was logged for this in the last 24 h${s.recently_logged_at ? ` (${formatDT(s.recently_logged_at, { timezone: tz, seconds: false })})` : ''} — logging again will need confirmation`} data-testid={`service-bulk-recent-${s.id}`}>
                                  <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> logged in last 24h
                                </Badge>
                              )}
                            </div>
                            <p className="text-[11px] text-muted-foreground">{s.task_name} • Due at {dueLabel(s)} • {remainingLabel(s)}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {bulkStep === 2 && (
            <div className="flex-1 overflow-y-auto px-6 space-y-4">
              {/* Shared details */}
              <div className="rounded-md border p-3 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Shared details (applied to all)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Date serviced</Label><Input type="datetime-local" value={bulkShared.completed_date} onChange={(e) => setBulkShared((p) => ({ ...p, completed_date: e.target.value }))} className="h-9" data-testid="service-bulk-date" /></div>
                  <div>
                    <Label className="text-xs flex items-center gap-1">Cost (apply to all)<MaintHelpTip topic="bulk_cost" testId="service-help-bulk-cost" /></Label>
                    <div className="flex gap-2">
                      <Input type="number" min="0" step="0.01" value={bulkShared.cost} onChange={(e) => setBulkShared((p) => ({ ...p, cost: e.target.value }))} placeholder="0.00" className="h-9" data-testid="service-bulk-shared-cost" />
                      <Button type="button" variant="outline" size="sm" className="h-9 whitespace-nowrap" onClick={bulkApplyCostToAll} data-testid="service-bulk-apply-cost">Apply to all</Button>
                    </div>
                  </div>
                  <div><Label className="text-xs">Vendor</Label><Input value={bulkShared.vendor} onChange={(e) => setBulkShared((p) => ({ ...p, vendor: e.target.value }))} className="h-9" data-testid="service-bulk-vendor" /></div>
                  <div><Label className="text-xs">Performed by</Label><Input value={bulkShared.performed_by} onChange={(e) => setBulkShared((p) => ({ ...p, performed_by: e.target.value }))} className="h-9" data-testid="service-bulk-performed-by" /></div>
                </div>
                <div><Label className="text-xs">Notes</Label><Textarea value={bulkShared.notes} onChange={(e) => setBulkShared((p) => ({ ...p, notes: e.target.value }))} rows={2} data-testid="service-bulk-notes" /></div>
                <div>
                  <Label className="text-xs">Receipt / attachment (attached to every service)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="file" multiple id="bulk-file" className="hidden" onChange={(e) => { bulkUpload(e.target.files); e.target.value = ''; }} />
                    <Button type="button" variant="outline" size="sm" className="h-8" disabled={bulkUploading} onClick={() => document.getElementById('bulk-file').click()} data-testid="service-bulk-upload">
                      {bulkUploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Paperclip className="w-4 h-4 mr-1" />} Attach
                    </Button>
                    <span className="text-xs text-muted-foreground">{(bulkShared.attachments || []).length} file(s)</span>
                  </div>
                  {(bulkShared.attachments || []).map((a) => (
                    <div key={a.id || a.url} className="flex items-center gap-2 text-xs mt-1">
                      <Paperclip className="w-3 h-3" /><span className="truncate flex-1">{a.name}</span>
                      <button type="button" className="text-red-500" onClick={() => setBulkShared((p) => ({ ...p, attachments: p.attachments.filter((x) => (x.id || x.url) !== (a.id || a.url)) }))}><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Per-vehicle rows */}
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Per-vehicle readings & cost</p>
                {bulkSelectedIds.map((id) => {
                  const s = bulkSchedules.find((x) => x.id === id);
                  if (!s) return null;
                  const needsReading = usesOdometer(s.basis) || usesEngineHours(s.basis);
                  const row = bulkRows[id] || { reading: '', cost: '' };
                  return (
                    <div key={id} className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-end border rounded-md p-2.5" data-testid={`service-bulk-row-${id}`}>
                      <div className="min-w-0">
                        <span className="text-sm font-medium">{deviceLabel(s)}</span>
                        <p className="text-[11px] text-muted-foreground">{s.task_name}</p>
                      </div>
                      <div className="w-40">
                        <Label className="text-[10px] text-muted-foreground flex items-center gap-1">{needsReading ? (usesOdometer(s.basis) ? 'Odometer when serviced (km)' : 'Engine hours when serviced') : 'No reading needed'}{needsReading && <MaintHelpTip topic="log_reading" testId={`service-help-bulk-row-reading-${id}`} />}</Label>
                        {needsReading && s.current_value != null && !s.basis_source_off && (
                          <p className="text-[10px] text-sky-700 dark:text-sky-400 whitespace-nowrap" data-testid={`service-bulk-current-${id}`}>
                            Currently at: {Number(s.current_value).toLocaleString()} {usesOdometer(s.basis) ? 'km' : 'hrs'}
                          </p>
                        )}
                        <Input type="number" min="0" step="0.1" disabled={!needsReading} value={needsReading ? row.reading : ''} onChange={(e) => setBulkRows((p) => ({ ...p, [id]: { ...(p[id] || {}), reading: e.target.value } }))} placeholder={needsReading ? '0' : '—'} className="h-8 text-sm" data-testid={`service-bulk-reading-${id}`} />
                      </div>
                      <div className="w-28">
                        <Label className="text-[10px] text-muted-foreground flex items-center gap-1">Cost<MaintHelpTip topic="bulk_row_cost" testId={`service-help-bulk-row-cost-${id}`} /></Label>
                        <Input type="number" min="0" step="0.01" value={row.cost} onChange={(e) => setBulkRows((p) => ({ ...p, [id]: { ...(p[id] || {}), cost: e.target.value } }))} placeholder="0.00" className="h-8 text-sm" data-testid={`service-bulk-cost-${id}`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <DialogFooter className="px-6 py-4 border-t">
            {bulkStep === 1 ? (
              <>
                <Button variant="outline" onClick={() => setBulkOpen(false)} data-testid="service-bulk-cancel">Cancel</Button>
                <Button onClick={bulkGoDetails} disabled={!bulkSelectedIds.length} data-testid="service-bulk-next">Next — {bulkSelectedIds.length} selected</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setBulkStep(1)} data-testid="service-bulk-back">Back</Button>
                <Button onClick={() => submitBulk(false)} disabled={bulkSubmitting} data-testid="service-bulk-submit">
                  {bulkSubmitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />} Log {bulkSelectedIds.length} Service{bulkSelectedIds.length === 1 ? '' : 's'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* ── Log Service (complete) / Edit Service Log dialog ── */}
      <Dialog open={!!completeTarget} onOpenChange={(o) => { if (!o) closeCompleteDialog(); }}>
        <DialogContent className="w-[95vw] max-w-2xl" data-testid="service-complete-dialog">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3 pr-8">
              <DialogTitle>{editingHistory ? 'Edit Service Log' : 'Log Service'}</DialogTitle>
              {editingHistory ? <EditServiceLogHelpPanel /> : <LogServiceHelpPanel />}
            </div>
            <DialogDescription>{completeTarget?.task_name} — {completeTarget ? deviceLabel(completeTarget) : ''}</DialogDescription>
          </DialogHeader>
          {(() => {
            const basis = completeTarget?.basis;
            const showMetrics = basis !== 'calendar';
            const odoOn = usesOdometer(basis);
            const hrsOn = usesEngineHours(basis);
            // Live source-aware reading of the vehicle (same value the Schedules list uses) — reference only.
            const currentRef = editingHistory ? null : (completeTarget?.current_value ?? null);
            const entered = odoOn ? completeForm.odometer_at_service : completeForm.engine_hours_at_service;
            const readingAbove = currentRef != null && entered !== '' && entered != null && Number(entered) > Number(currentRef);
            const svcUnit = odoOn ? 'km' : 'hrs';
            const fixed = !editingHistory && showMetrics && (completeTarget?.fixed_schedule ?? false) && completeTarget?.recurring;
            const interval = Number(completeTarget?.interval_value) || 0;
            const anchor = completeTarget?.schedule_anchor_value != null ? Number(completeTarget.schedule_anchor_value) : (completeTarget?.baseline_value != null ? Number(completeTarget.baseline_value) : null);
            const overrideNum = completeForm.next_due_override !== '' && completeForm.next_due_override != null ? Number(completeForm.next_due_override) : null;
            let predictedNextDue = null;
            if (!editingHistory && showMetrics && entered !== '' && entered != null && !Number.isNaN(Number(entered))) {
              const r = Number(entered);
              if (overrideNum != null && !Number.isNaN(overrideNum)) predictedNextDue = overrideNum;
              else if (fixed && interval > 0 && anchor != null) predictedNextDue = Math.round((anchor + Math.round((r - anchor) / interval) * interval) + interval);
              else if (interval > 0) predictedNextDue = Math.round(r + interval);
            }
            return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">Serviced Date &amp; Time</Label>
                <Input type="datetime-local" value={completeForm.completed_date || ''} onChange={(e) => setCompleteForm(p => ({ ...p, completed_date: e.target.value }))} className="h-9" data-testid="service-complete-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">Cost<MaintHelpTip topic="log_cost" testId="service-help-log-cost" /></Label>
                <Input type="number" step="0.01" value={completeForm.cost} onChange={(e) => setCompleteForm(p => ({ ...p, cost: e.target.value }))} placeholder="0.00" className="h-9" data-testid="service-complete-cost" />
              </div>
              {showMetrics && (
                <>
                  <div className={`space-y-1 ${odoOn ? '' : 'opacity-50'}`}>
                    <Label className="text-xs flex items-center gap-1">Odometer when serviced (km){odoOn && <span className="text-red-500"> *</span>}<MaintHelpTip topic="log_reading" testId="service-help-log-odometer" /></Label>
                    <Input type="number" value={completeForm.odometer_at_service} onChange={(e) => setCompleteForm(p => ({ ...p, odometer_at_service: e.target.value }))} disabled={!odoOn} placeholder={odoOn ? 'Enter reading at service' : 'Not used by this schedule'} className="h-9" data-testid="service-complete-odometer" />
                    {odoOn && currentRef != null && (
                      <p className={`text-[10px] ${readingAbove ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-muted-foreground'}`} data-testid="service-complete-odometer-ref">
                        {readingAbove
                          ? (fixed
                            ? `Entered reading is above the vehicle's current ${currentRef} km — the fixed schedule will still snap the next service to the grid. Please double-check.`
                            : `Entered reading is above the vehicle's current ${currentRef} km — next due will be ${Number(completeForm.odometer_at_service) - currentRef} km further out than expected. Please double-check.`)
                          : `For reference — vehicle's current odometer: ${currentRef} km`}
                      </p>
                    )}
                  </div>
                  <div className={`space-y-1 ${hrsOn ? '' : 'opacity-50'}`}>
                    <Label className="text-xs flex items-center gap-1">Engine Hours when serviced{hrsOn && <span className="text-red-500"> *</span>}<MaintHelpTip topic="log_reading" testId="service-help-log-hours" /></Label>
                    <Input type="number" value={completeForm.engine_hours_at_service} onChange={(e) => setCompleteForm(p => ({ ...p, engine_hours_at_service: e.target.value }))} disabled={!hrsOn} placeholder={hrsOn ? 'Enter hours at service' : 'Not used by this schedule'} className="h-9" data-testid="service-complete-hours" />
                    {hrsOn && currentRef != null && (
                      <p className={`text-[10px] ${readingAbove ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-muted-foreground'}`} data-testid="service-complete-hours-ref">
                        {readingAbove
                          ? `Entered hours are above the vehicle's current ${currentRef} hrs — please double-check.`
                          : `For reference — vehicle's current engine hours: ${currentRef} hrs`}
                      </p>
                    )}
                  </div>
                </>
              )}
              <div className="space-y-1">
                <Label className="text-xs">Vendor</Label>
                <Input value={completeForm.vendor} onChange={(e) => setCompleteForm(p => ({ ...p, vendor: e.target.value }))} className="h-9" data-testid="service-complete-vendor" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Performed By</Label>
                <Input value={completeForm.performed_by} onChange={(e) => setCompleteForm(p => ({ ...p, performed_by: e.target.value }))} className="h-9" data-testid="service-complete-by" />
              </div>
            </div>
            {showMetrics && !editingHistory && (
              <div className="space-y-1 rounded-md border border-border bg-muted/30 px-3 py-2">
                <Label className="text-xs flex items-center gap-1">Set next service at ({svcUnit}) — optional override<MaintHelpTip topic="next_override" testId="service-help-next-override" /></Label>
                <Input type="number" value={completeForm.next_due_override} onChange={(e) => setCompleteForm(p => ({ ...p, next_due_override: e.target.value }))} placeholder="Leave blank to use the schedule's normal calculation" className="h-9" data-testid="service-complete-next-due-override" />
                {predictedNextDue != null && (
                  <p className="text-[10px] text-emerald-700 dark:text-emerald-400" data-testid="service-complete-next-due-hint">
                    Next service will be scheduled at <b>{predictedNextDue.toLocaleString()} {svcUnit}</b>
                    {overrideNum != null ? ' (manual override)' : fixed ? ' (fixed schedule)' : ' (rolling)'}
                  </p>
                )}
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Textarea value={completeForm.notes} onChange={(e) => setCompleteForm(p => ({ ...p, notes: e.target.value }))} rows={2} data-testid="service-complete-notes" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Attachments (invoices / photos / documents)</Label>
              <div className="flex items-center gap-2">
                <input type="file" id="svc-file" className="hidden" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.csv,.txt" onChange={(e) => { uploadAttachment(e.target.files); e.target.value = ''; }} data-testid="service-complete-file-input" />
                <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('svc-file').click()} disabled={uploading} data-testid="service-complete-upload-btn">
                  {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Add files
                </Button>
                <span className="text-[11px] text-muted-foreground">{(completeForm.attachments || []).length ? `${completeForm.attachments.length} attached` : 'No files yet'}</span>
              </div>
              {(completeForm.attachments || []).length > 0 && (
                <ul className="mt-1 divide-y rounded-md border text-xs" data-testid="service-complete-attachment-list">
                  {completeForm.attachments.map((a, i) => (
                    <li key={a.id || a.url || i} className="flex items-center gap-2 px-2 py-1.5">
                      <Paperclip className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                      <button type="button" className="flex-1 min-w-0 truncate text-left text-blue-600 dark:text-blue-400 hover:underline" onClick={() => openAttachment(a)} data-testid={`service-complete-attachment-${i}`}>{a.name || 'Attachment'}</button>
                      {a.size != null && <span className="text-muted-foreground flex-shrink-0">{fmtSize(a.size)}</span>}
                      <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-red-500 flex-shrink-0" onClick={() => setPendingRemoveAtt(a)} title="Remove (deletes the file)" data-testid={`service-complete-attachment-remove-${i}`}><X className="w-3 h-3" /></Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {editingHistory && (
              <div className="rounded-md border bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground space-y-0.5" data-testid="service-complete-audit">
                <div>Logged by <span className="font-medium text-foreground">{editingHistory.created_by_name || 'Unknown'}</span> on {formatDT(editingHistory.created_at, { timezone: tz, seconds: false })}</div>
                {editingHistory.updated_at && <div>Last edited by <span className="font-medium text-foreground">{editingHistory.updated_by_name || 'Unknown'}</span> on {formatDT(editingHistory.updated_at, { timezone: tz, seconds: false })}</div>}
              </div>
            )}
          </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={closeCompleteDialog} data-testid="service-complete-cancel">Cancel</Button>
            <Button size="sm" onClick={() => submitComplete(false)} disabled={completing || uploading} data-testid="service-complete-submit">
              {completing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />} {editingHistory ? 'Save Changes' : 'Log Service'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Attachments viewer ── */}
      <Dialog open={!!attachmentsFor} onOpenChange={(o) => { if (!o) setAttachmentsFor(null); }}>
        <DialogContent className="w-[95vw] max-w-md" data-testid="service-attachments-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Paperclip className="w-4 h-4" /> {attachmentsFor?.completed_date ? 'Attachments' : 'Schedule Documents'}</DialogTitle>
            <DialogDescription>{attachmentsFor?.task_name} — {attachmentsFor ? (attachmentsFor.asset_name || attachmentsFor.device_name || 'Device') : ''}{attachmentsFor?.completed_date ? ` • ${formatDT(attachmentsFor.completed_date, { timezone: tz, seconds: false })}` : ''}</DialogDescription>
          </DialogHeader>
          <ul className="divide-y rounded-md border text-sm" data-testid="service-attachments-list">
            {(attachmentsFor?.attachments || []).map((a, i) => (
              <li key={a.id || a.url || i} className="flex items-center gap-2 px-3 py-2">
                <Paperclip className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <button type="button" className="flex-1 min-w-0 text-left truncate text-blue-600 dark:text-blue-400 hover:underline" onClick={() => openAttachment(a)} title="Open in new tab" data-testid={`service-attachment-open-${i}`}>{a.name || 'Attachment'}</button>
                <span className="text-[11px] text-muted-foreground flex-shrink-0">{[a.content_type?.split('/')[1]?.toUpperCase(), fmtSize(a.size)].filter(Boolean).join(' • ')}</span>
                <Button type="button" size="icon" variant="outline" className="h-8 w-8 flex-shrink-0" onClick={() => downloadAttachment(a)} title="Download" data-testid={`service-attachment-download-${i}`}><Download className="w-4 h-4" /></Button>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setAttachmentsFor(null)} data-testid="service-attachments-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DuplicateScheduleDialog schedule={duplicating} devices={devices} onClose={() => setDuplicating(null)} onDone={() => { fetchSchedules(); fetchSummary(); }} />
      <ConfirmActionDialog
        open={!!pendingDelete}
        onOpenChange={(o) => { if (!o) setPendingDelete(null); }}
        title={pendingDelete?.kind === 'schedule' ? 'Delete Service Schedule?' : 'Delete Service Log?'}
        message={pendingDelete?.kind === 'schedule'
          ? <>Are you sure you want to delete <b>{pendingDelete?.item?.task_name}</b> for <b>{pendingDelete?.item ? deviceLabel(pendingDelete.item) : ''}</b>? Any open alert for it will be resolved. This action cannot be undone.</>
          : <>Are you sure you want to delete the <b>{pendingDelete?.item?.task_name}</b> log for <b>{pendingDelete?.item?.asset_name || pendingDelete?.item?.device_name}</b> ({pendingDelete?.item ? formatDT(pendingDelete.item.completed_date, { timezone: tz, seconds: false }) : ''})? {pendingDelete?.item?.attachment_count ? `${pendingDelete.item.attachment_count} attached file(s) will be deleted from storage. ` : ''}This action cannot be undone.</>}
        requireTyped
        onConfirm={confirmDelete}
        testPrefix="service-delete"
      />
      <ConfirmActionDialog
        open={!!pendingRemoveAtt}
        onOpenChange={(o) => { if (!o) setPendingRemoveAtt(null); }}
        title={pendingRemoveAtt?.kind === 'schedule' ? 'Remove document?' : 'Remove attachment?'}
        message={<>Remove <b>{pendingRemoveAtt?.kind === 'schedule' ? pendingRemoveAtt?.att?.name : pendingRemoveAtt?.name}</b>? The file will be permanently deleted from storage.</>}
        confirmLabel="Remove"
        onConfirm={() => (pendingRemoveAtt?.kind === 'schedule' ? removeScheduleDoc(pendingRemoveAtt.att) : removeAttachment(pendingRemoveAtt))}
        testPrefix="service-attachment-remove-confirm"
      />
      <ConfirmActionDialog
        open={!!dupConfirm}
        onOpenChange={(o) => { if (!o) setDupConfirm(null); }}
        title="Log this service again?"
        message={<>A service for this schedule was already logged {dupConfirm?.logged_at ? `on ${formatDT(dupConfirm.logged_at, { timezone: tz, seconds: false })}` : 'recently'}{dupConfirm?.logged_by ? ` by ${dupConfirm.logged_by}` : ''}. Do you want to log another one?</>}
        confirmLabel="Log anyway"
        destructive={false}
        onConfirm={() => submitComplete(true)}
        testPrefix="service-duplicate-log"
      />
      <ConfirmActionDialog
        open={!!bulkDupConfirm}
        onOpenChange={(o) => { if (!o) { setBulkDupConfirm(null); setBulkOpen(false); } }}
        title={`Log ${bulkDupConfirm?.length || 0} service${(bulkDupConfirm?.length || 0) === 1 ? '' : 's'} again?`}
        message={<>
          {(bulkDupConfirm?.length || 0) === 1 ? 'This vehicle' : `These ${bulkDupConfirm?.length} vehicles`} already had a service logged in the last 24 hours, so {(bulkDupConfirm?.length || 0) === 1 ? 'it was' : 'they were'} skipped:
          <span className="block mt-2 text-xs text-muted-foreground max-h-40 overflow-y-auto">
            {(bulkDupConfirm || []).map((r) => (
              <span key={r.schedule_id} className="block">• {r.device_name || 'Vehicle'} — {r.task_name || 'service'}</span>
            ))}
          </span>
          <span className="block mt-2">Log {(bulkDupConfirm?.length || 0) === 1 ? 'it' : 'them'} anyway?</span>
        </>}
        confirmLabel="Log anyway"
        destructive={false}
        onConfirm={() => { setBulkDupConfirm(null); submitBulk(true); }}
        testPrefix="service-bulk-duplicate-log"
      />

      <NotificationTemplateModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        api={api}
        initialType={templateInitialType}
        onTemplatesChanged={fetchTemplates}
      />
    </div>
  );
};

export default MaintenanceTab;
